import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Maximize, Minimize } from 'lucide-react';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import AnimalAlphabetChatEmbed from './AnimalAlphabetChatEmbed';
import { TeachingFacilitation } from '@/components/TeachingFacilitation';
import type { HomeGameScreen } from '@/data/homeGameCards';
import {
  getWelcomeBlock,
  getPullBlock,
  microLabels,
  playTogetherHubFollowUpChips,
  welcomeAssistantNeedsResync,
  PLAY_TOGETHER_MICRO_SCRIPT_REVISION,
} from '@/data/playTogetherMicroOnboarding';
import {
  QUICK_REPLY_SIM_KIND,
  GAME_SIM_KIND_TO_HOME_SCREEN,
  type GameSimKind,
  type PlaySimStep,
  type AnimalCharacter,
  ANIMAL_CHARACTERS,
  SKILL_DESIGN_TOKENS,
  getAnimalSelectionPrompt,
  getFirstInputPromptWithAnimal,
  getConversationalSkillPrompt,
  getSkillAcknowledgment,
  getSkillDesignToken,
  getSimulationBeat0,
  getSimulationBeat1Skills,
  getSimulationBeat2Caregiver,
  getSimulationCaregiverPhaseWrapUp,
  getSimulationConstructiveCaregiverFeedback,
  getSimulationConstructiveCaregiverFeedbackRound2,
  getSimulationCaregiverSoftLanding,
  assessCaregiverResponseForKind,
  PLAY_SIM_MAX_COACH_MESSAGES,
  literacySkillChoiceLabels,
} from '@/data/playTogetherGameSimulations';
import PlayTogetherGameCardsGrid from './PlayTogetherGameCardsGrid';
import {
  WG_PANEL_SURFACE,
  wgHeaderTitleStyle,
  wgHeaderSubtitleStyle,
  wgBodyStyle,
  wgChromeMin,
  WG_TEXT,
} from './playTogetherPanelTokens';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '@/lib/utils';

/** Drop any messages after the last matching user line, then callers append one assistant (dedupes double fetch). */
function sliceThroughLastUserMessage(
  messages: { role: 'user' | 'assistant'; content: string }[],
  userContent: string
): { role: 'user' | 'assistant'; content: string }[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user' && messages[i].content === userContent) {
      return messages.slice(0, i + 1);
    }
  }
  return messages;
}

type ChatTurn = { role: 'user' | 'assistant'; content: string };

/** One assistant bubble after the last user line matching `userExactContent`; strips extra assistants (Strict Mode / double events). */
function singleAssistantAfterLastUser(
  prev: ChatTurn[],
  userExactContent: string,
  assistantContent: string
): ChatTurn[] {
  const head = sliceThroughLastUserMessage(prev, userExactContent);
  const last = prev[prev.length - 1];
  if (
    last?.role === 'assistant' &&
    prev.length === head.length + 1 &&
    last.content === assistantContent
  ) {
    return prev;
  }
  return [...head, { role: 'assistant', content: assistantContent }];
}

/** Detect if message is a skill prompt and return color */
function getSkillPromptColor(text: string): string | null {
  if (text.includes('👀 Observe & Describe') || text.includes('👀 Observar y Describir')) {
    console.log('🎨 DESCRIBE COLOR DETECTED');
    return SKILL_DESIGN_TOKENS.describe.color;
  }
  if (text.includes('🤝 Connect & Reflect') || text.includes('🤝 Conectar y Reflejar')) {
    console.log('🎨 REFLECT COLOR DETECTED');
    return SKILL_DESIGN_TOKENS.reflect.color;
  }
  if (text.includes('✨ Compliment & Praise') || text.includes('✨ Elogiar y Valorar')) {
    console.log('🎨 PRAISE COLOR DETECTED');
    return SKILL_DESIGN_TOKENS.praise.color;
  }
  if (text.includes('🌿 Associate & Relate') || text.includes('🌿 Asociar y Relacionar')) {
    console.log('🎨 RELATE COLOR DETECTED');
    return SKILL_DESIGN_TOKENS.relate.color;
  }
  if (text.includes('💚 Care for All Life') || text.includes('💚 Cuida toda la vida')) {
    console.log('🎨 CARE COLOR DETECTED');
    return SKILL_DESIGN_TOKENS.care.color;
  }
  return null;
}

/** Renders markdown-like formatting (bold, italic, paragraphs, line breaks) as React nodes */
function formatMessageContent(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const paragraphs = text.split(/\n\n+/);
  paragraphs.forEach((para, pIndex) => {
    if (pIndex > 0) parts.push(<div key={`gap-${pIndex}`} style={{ height: 12 }} />);
    const lines = para.split('\n');
    lines.forEach((line, lIndex) => {
      if (lIndex > 0) parts.push(<br key={`br-${pIndex}-${lIndex}`} />);
      const tokens: React.ReactNode[] = [];
      let remainder = line;
      let key = 0;
      while (remainder.length > 0) {
        const bold = remainder.match(/^\*\*(.+?)\*\*/);
        const italic = remainder.match(/^\*(.+?)\*/);
        if (bold) {
          tokens.push(<strong key={key++}>{bold[1]}</strong>);
          remainder = remainder.slice(bold[0].length);
        } else if (italic && !remainder.startsWith('**')) {
          tokens.push(<em key={key++}>{italic[1]}</em>);
          remainder = remainder.slice(italic[0].length);
        } else {
          const next = remainder.match(/\*\*|\*/);
          const plain = next ? remainder.slice(0, next.index) : remainder;
          if (plain) tokens.push(plain);
          remainder = next ? remainder.slice(next.index!) : '';
        }
      }
      parts.push(<span key={`line-${pIndex}-${lIndex}`}>{tokens}</span>);
    });
  });
  return <>{parts}</>;
}

// Play Together / Juguemos Juntos
// Floating Hub Overlay for Wonderpath Creative EcoGames
// Emphasizing circularity, resourcefulness, and playing with what you have

interface PlayTogetherFloatingHubProps {
  /** First visit on homepage: open almost full-page onboarding */
  initialLandingOpen?: boolean;
  /** Persist “seen” + analytics parent hook */
  onLandingDismiss?: () => void;
  /** Navigate to a main game from landing preview cards */
  onNavigateToGame?: (screen: HomeGameScreen) => void;
}

const PlayTogetherFloatingHub: React.FC<PlayTogetherFloatingHubProps> = ({
  initialLandingOpen = false,
  onLandingDismiss,
  onNavigateToGame,
}) => {
  const { language, setLanguage } = useLanguage();
  const lang = language;
  /** Re-run welcome sync whenever scripted copy changes (not only when revision is bumped). */
  const expectedWelcomeContent = getWelcomeBlock(lang);
  const [isHubOpen, setIsHubOpen] = useState(initialLandingOpen);
  const [landingWelcomeVisible, setLandingWelcomeVisible] = useState(initialLandingOpen);
  /** Tracks prior prop so we can open welcome when initialLandingOpen flips to true */
  const prevInitialLandingOpenRef = useRef(false);
  const [hubView, setHubView] = useState<'guide' | 'ask' | 'teacher-facilitation'>('ask');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [modalType, setModalType] = useState<'skill' | 'gameTip' | 'activity' | null>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);
  const [expandedSkillId, setExpandedSkillId] = useState<string | null>(null);
  const [expandedGameTipId, setExpandedGameTipId] = useState<string | null>(null);
  const [expandedSkillNoteId, setExpandedSkillNoteId] = useState<string | null>(null);

  const hubContainerRef = useRef<HTMLDivElement>(null);
  const [onboardingMicroStep, setOnboardingMicroStep] = useState<
    'idle' | 'awaiting_ready' | 'awaiting_pull' | 'show_games'
  >(() => (initialLandingOpen ? 'awaiting_ready' : 'idle'));
  /** Hides "Start Guidance" while the pull line is loading from the API */
  const [onboardingAwaitingPullAi, setOnboardingAwaitingPullAi] = useState(false);
  /** Prevents double submit / duplicate assistant bubbles from rapid taps */
  const onboardingPullRequestLockRef = useRef(false);
  const onboardingBridgeRequestLockRef = useRef(false);
  /** Mirrors `chatMessages` for building API payloads without reading stale render closures. */
  const chatMessagesRef = useRef<{ role: 'user' | 'assistant'; content: string }[]>([]);
  /** Ignore late responses if the user replayed onboarding or a newer pull started. */
  const onboardingPullGenRef = useRef(0);
  const onboardingBridgeGenRef = useRef(0);
  /** Synchronous guard — `isChatLoading` can lag a render, so double Send was still possible. */
  const playSimChatSendBusyRef = useRef(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>(() =>
    initialLandingOpen ? [{ role: 'assistant', content: getWelcomeBlock(language) }] : []
  );

  useLayoutEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);

  /** Scripted play simulation for quick replies 0–3 (literacy, breathing, draw, express) */
  const [playSim, setPlaySim] = useState<null | {
    kind: GameSimKind;
    step: PlaySimStep;
    animal?: AnimalCharacter;
    childObservation?: string;
    skillResponses?: {
      describe?: string;
      reflect?: string;
      praise?: string;
      relate?: string;
      care?: string;
    };
  }>(null);
  /** 0 = first caregiver try; 1 = coaching already shown (next send continues to beat 3) */
  const playSimCaregiverCoachRoundRef = useRef(0);
  /** Index in chatMessages where the current scripted sim transcript begins (for AI coach context) */
  const playSimThreadStartRef = useRef(0);

  const t = (en: string, es: string) => lang === 'en' ? en : es;

  /** Play skill chime sound using Web Audio API */
  const playSkillChime = (frequency: number) => {
    console.log(`🎵 Playing chime at ${frequency} Hz`);
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.8);
      console.log('✅ Chime played successfully');
    } catch (err) {
      console.error('❌ Audio error:', err);
    }
  };

  /** Play celebration chord (C-E-G-C major) */
  const playCelebrationChord = () => {
    console.log('🎉 Playing celebration chord!');
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const frequencies = [261.63, 329.63, 392.0, 523.25]; // C4, E4, G4, C5

      frequencies.forEach((freq, i) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = freq;
        oscillator.type = 'sine';

        const startTime = audioContext.currentTime + i * 0.15;
        gainNode.gain.setValueAtTime(0.2, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 1.2);

        oscillator.start(startTime);
        oscillator.stop(startTime + 1.2);
      });
      console.log('✅ Celebration chord playing');
    } catch (err) {
      console.error('❌ Audio error:', err);
    }
  };

  const speechRec = useSpeechRecognition({
    lang: lang === 'en' ? 'en-US' : 'es-ES',
    onFinal: (text) => {
      setChatInput((prev) => {
        const p = prev.trim();
        return p ? `${p} ${text}` : text;
      });
    },
  });

  const playSimSpeechActive =
    !!playSim &&
    (playSim.step === 'await_first_input' ||
      playSim.step === 'await_skill_choice' ||
      playSim.step === 'await_caregiver' ||
      playSim.step === 'await_skill_describe' ||
      playSim.step === 'await_skill_reflect' ||
      playSim.step === 'await_skill_praise' ||
      playSim.step === 'await_skill_relate' ||
      playSim.step === 'await_skill_care');

  useEffect(() => {
    if (initialLandingOpen && !prevInitialLandingOpenRef.current) {
      setIsHubOpen(true);
      setLandingWelcomeVisible(true);
      setOnboardingMicroStep('awaiting_ready');
      const welcome = [{ role: 'assistant' as const, content: getWelcomeBlock(lang) }];
      chatMessagesRef.current = welcome;
      setChatMessages(welcome);
      setPlaySim(null);
      playSimCaregiverCoachRoundRef.current = 0;
    }
    prevInitialLandingOpenRef.current = initialLandingOpen;
  }, [initialLandingOpen, lang]);

  /**
   * Keep the welcome bubble in sync with `getWelcomeBlock` (language + any copy change).
   * useLayoutEffect + normalized compare + legacy-phrase detection so stale/HMR/old **bold** variants
   * cannot stick. If there are no user messages yet, normalize to a single fresh welcome.
   */
  useLayoutEffect(() => {
    if (!landingWelcomeVisible || onboardingMicroStep !== 'awaiting_ready') return;
    setChatMessages((prev) => {
      if (prev.some((m) => m.role === 'user')) return prev;
      if (prev.length === 0 || prev[0]?.role !== 'assistant') return prev;
      const fresh = getWelcomeBlock(lang);
      if (prev.length === 1 && !welcomeAssistantNeedsResync(prev[0].content, fresh)) return prev;
      const one = [{ role: 'assistant' as const, content: fresh }];
      chatMessagesRef.current = one;
      return one;
    });
  }, [
    lang,
    landingWelcomeVisible,
    onboardingMicroStep,
    expectedWelcomeContent,
    PLAY_TOGETHER_MICRO_SCRIPT_REVISION,
  ]);

  // Messages already sent by user (from buttons or typed) - hide those suggestions to show mindful awareness of their journey
  const usedSuggestionTexts = new Set(
    chatMessages.filter((m) => m.role === 'user').map((m) => m.content)
  );
  const filterUsedSuggestions = <T extends { en: string; es: string }>(opts: T[]) =>
    opts.filter((opt) => !usedSuggestionTexts.has(t(opt.en, opt.es)));

  // Show follow-up quick buttons only when NOT mid-simulation; show after simulation ends or on conversation shift/slowness
  const shouldShowFollowUpButtons = (): boolean => {
    if (playSim && playSim.step !== 'complete') return false;
    const last = chatMessages[chatMessages.length - 1];
    if (!last || last.role !== 'assistant') return false;
    const text = last.content.toLowerCase();

    // Mid-simulation indicators: role-play prompts, awaiting user action
    const midSimulationPhrases = [
      'what do you say', 'qué dices', 'what did you say', 'qué dijiste',
      "i'm stacking", 'estoy apilando', "i'm playing", 'estoy jugando',
      'try describe', 'intenta describir', 'now try reflect', 'ahora intenta reflejar',
      'mirror what i', 'imita lo que', 'add praise', 'agrega un elogio',
      'add reflect', 'agrega reflejar', 'stack yours', 'apila los tuyos',
      'what did you notice', 'qué notaste', 'your turn', 'tu turno',
      "i'll play the child", 'jugaré el niño', "i'll be the child", 'seré el niño',
    ];
    const isMidSimulation = midSimulationPhrases.some((p) => text.includes(p));

    // Simulation wrap-up or shift: invitation to switch, celebration, explore next
    const wrapUpPhrases = [
      'would you like to switch', 'te gustaría cambiar', 'switch roles', 'cambiar roles',
      'what would you like to try', 'qué te gustaría intentar', 'what would you like to explore',
      'celebrate', 'celebrar', 'great job', 'excelente', 'plan with your child',
      'help with ideas or preparation', 'ayuda con ideas', 'explore next', 'explorar después',
      'what can i explore', 'qué más puedo explorar',
    ];
    const isSimulationCompleteOrShift = wrapUpPhrases.some((p) => text.includes(p));

    // Slowness: conversation has depth (many exchanges)
    const hasConversationDepth = chatMessages.length >= 8;

    if (isMidSimulation && !hasConversationDepth) return false;
    return isSimulationCompleteOrShift || hasConversationDepth;
  };

  // Attribution & Origin Story
  const attribution = {
    en: {
      title: "About This Resource",
      story: "The 5 skills and these games are Wonderpath's approach to blend the underlying practices (describing, reflecting, praising) common in Parent-Child Interaction Therapy (PCIT) and play therapy with arts, design, and sustainability.",
      context: "I learned about the Parent-Child Interaction Therapy (PCIT) through my experience facilitating the CARING at Columbia Head Start program, a wonderful 12-week creative arts and play program developed by Dr. William Wu, Robin Snow, and their team at Columbia University. CARING at Columbia was founded in 1988 by Clarice J. Kestenbaum, MD and Ian Canino, MD as a way to promote the mental health of children and adolescents living in New York's underserved communities. Since then, CARING has touched the lives of thousands of youth and families, helping them to cope with stress and express their feelings through the creative arts and play.",
      gratitude: "I'm deeply grateful for what that program taught me about the power of creative play in early childhood. This adaptation shares those core insights with families in a new format.",
      encourage: "If you'd like to learn more about the original program, I encourage you to explore their work.",
      link: "CARING at Columbia",
      disclaimer: "This is an independent adaptation, not affiliated with Columbia University or CARING at Columbia."
    },
    es: {
      title: "Sobre Este Recurso",
      story: "Las 5 habilidades y estos juegos son el enfoque de Wonderpath para combinar las prácticas subyacentes (describir, reflejar, elogiar) comunes en la Terapia de Interacción Padres-Hijos (PCIT) y la terapia de juego con las artes, el diseño y la sostenibilidad.",
      context: "Aprendí sobre la Terapia de Interacción Padres-Hijos (PCIT) a través de mi experiencia facilitando el programa CARING at Columbia Head Start, un maravilloso programa de 12 semanas de artes creativas y juego desarrollado por el Dr. William Wu, Robin Snow y su equipo en la Universidad de Columbia. CARING at Columbia fue fundado en 1988 por Clarice J. Kestenbaum, MD e Ian Canino, MD como una forma de promover la salud mental de niños y adolescentes que viven en comunidades desatendidas de Nueva York. Desde entonces, CARING ha tocado las vidas de miles de jóvenes y familias, ayudándolos a sobrellevar el estrés y expresar sus sentimientos a través de las artes creativas y el juego.",
      gratitude: "Estoy profundamente agradecido/a por lo que ese programa me enseñó sobre el poder del juego creativo en la primera infancia. Esta adaptación comparte esas ideas centrales con las familias en un nuevo formato.",
      encourage: "Si deseas aprender más sobre el programa original, te animo a explorar su trabajo.",
      link: "CARING at Columbia",
      disclaimer: "Esta es una adaptación independiente, no afiliada con la Universidad de Columbia o CARING at Columbia."
    }
  };

  // Design System
  const gradients = {
    teal: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
    purple: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
    pink: 'linear-gradient(135deg, #F472B6 0%, #DB2777 100%)',
    orange: 'linear-gradient(135deg, #FB923C 0%, #EA580C 100%)',
    green: 'linear-gradient(135deg, #4ADE80 0%, #16A34A 100%)',
    amber: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
    coral: 'linear-gradient(135deg, #F9A8D4 0%, #EC4899 100%)',
    cyan: 'linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%)',
    earth: 'linear-gradient(135deg, #A3E635 0%, #65A30D 100%)',
  };

  // Resourcefulness Philosophy
  const resourcefulnessIntro = {
    en: {
      title: "Play with what you have",
      subtitle: "No fancy toys needed",
      description: "The best play comes from imagination, not shopping. Look around your home—you already have everything you need. Repurpose, recycle, reimagine.",
      tips: [
        "🥫 Containers become drums, towers, or puppet stages",
        "🧦 Old socks become puppets, not trash",
        "📦 Boxes become worlds—caves, houses, boats",
        "🍂 Nature offers free art supplies—leaves, sticks, stones",
        "📰 Paper destined for recycling becomes drawing canvases"
      ]
    },
    es: {
      title: "Juega con lo que tienes",
      subtitle: "No necesitas juguetes caros",
      description: "El mejor juego viene de la imaginación, no de las compras. Mira alrededor de tu casa—ya tienes todo lo que necesitas. Reutiliza, recicla, reimagina.",
      tips: [
        "🥫 Los contenedores se convierten en tambores, torres o escenarios",
        "🧦 Los calcetines viejos se convierten en títeres, no en basura",
        "📦 Las cajas se convierten en mundos—cuevas, casas, barcos",
        "🍂 La naturaleza ofrece materiales gratis—hojas, palitos, piedras",
        "📰 El papel para reciclar se convierte en lienzos para dibujar"
      ]
    }
  };

  // The 5 Play Skills - WonderGuidance Sequence
  const playSkills = [
    {
      id: 'describe',
      icon: '👁️',
      gradient: gradients.teal,
      name: { en: "Observe and describe", es: "Observar y describir" },
      phrase: { en: "I see you...", es: "Veo que tú..." },
      short: {
        en: "Narrate what your child does. Describing reuse or nature shows that caring for the planet is normal.",
        es: "Narra lo que hace tu hijo. Describir reutilizar o la naturaleza muestra que cuidar el planeta es normal."
      },
      full: {
        en: "Say out loud what they're doing. Don't direct—just describe. When you notice reuse, nature, or kindness to others, children learn to value life and our planet.",
        es: "Di en voz alta lo que hacen. No dirijas—solo describe. Cuando notas reutilizar, naturaleza o bondad, los niños aprenden a valorar la vida y el planeta."
      },
      examples: {
        en: ["I see you putting the bottle cap in the recycle bin!", "You're stacking those containers high—they look like a tower!", "You chose the bumpy rock. It feels different from the smooth one.", "You're making swirls in the sand with your finger!"],
        es: ["¡Veo que pones la tapa en el bote de reciclaje!", "¡Estás apilando esos contenedores alto—parecen una torre!", "Elegiste la piedra con textura. Se siente diferente a la lisa.", "¡Estás haciendo espirales en la arena con tu dedo!"]
      },
      tip: {
        en: "Focus on what IS happening. Caring for planet = normal, valued.",
        es: "Enfócate en lo que ESTÁ pasando. Cuidar el planeta = normal, valorado."
      }
    },
    {
      id: 'praise',
      icon: '✨',
      gradient: gradients.amber,
      name: { en: "Compliment and praise", es: "Cumplimentar y elogiar" },
      phrase: { en: "I love how you...", es: "Me encanta cómo..." },
      short: {
        en: "Praise specifically. Praising reuse and care for nature builds confidence and sustainable habits.",
        es: "Elogia con detalle. Elogiar reutilizar y cuidar la naturaleza construye confianza y hábitos sostenibles."
      },
      full: {
        en: "Name what you liked. 'I love how you reused that box!' or 'You remembered to turn off the water!' Specific praise builds self-love and gratitude for life.",
        es: "Nombra lo que te gustó. '¡Me encanta cómo reutilizaste esa caja!' o '¡Recordaste cerrar el agua!' El elogio específico construye amor propio y gratitud por la vida."
      },
      examples: {
        en: ["I love how you reused that old box for your house!", "You figured out how to fix the torn paper—that's being resourceful!", "What a creative way to use those leaves as wings!", "You remembered to turn off the water. That helps our planet."],
        es: ["¡Me encanta cómo reutilizaste esa caja vieja para tu casa!", "¡Descubriste cómo arreglar el papel roto—eso es ser recursivo!", "¡Qué forma creativa de usar esas hojas como alas!", "Recordaste cerrar el agua. Eso ayuda a nuestro planeta."]
      },
      tip: {
        en: "Praise process. Builds confidence + gratitude for life.",
        es: "Elogia el proceso. Construye confianza + gratitud por la vida."
      },
      note: {
        en: {
          title: `Best Practices in Summary`,
          content: `Based on current research, the strongest recommendations for early childhood (roughly ages 1–7):

• Praise the process, not the person. Comment on effort, strategy, persistence, and approach—not innate traits.

• Be specific, not generic. "You kept trying different ways to make that fit" is more useful than "Good job!" which is effectively content-free.

• Avoid inflation. Genuine, modest acknowledgment is more developmentally supportive than superlatives.

• Go beyond effort. Simply saying "you worked hard" isn't enough—help children identify what strategies worked and encourage exploring alternatives when something doesn't.

• Watch your "easy" language. Framing tasks as easy for the child ("that's so easy for you!") attributes performance to fixed ability rather than process.

• Be conscious of gender. Girls statistically receive less process praise and more person praise, contributing to different motivational patterns—worth actively correcting.

• Model failure as a learning event. Research by Haimovitz & Dweck shows that children pick up on caregivers' beliefs about failure itself, not just their praise.

The overarching frame is that early childhood is a particularly sensitive period for forming these beliefs—and that praise is less about making children feel good in the moment and more about shaping their relationship with challenge, effort, and learning over the long arc.`
        },
        es: {
          title: `Mejores Prácticas en Resumen`,
          content: `Según la investigación actual, las recomendaciones más importantes para la primera infancia (aproximadamente de 1 a 7 años):

• Elogia el proceso, no a la persona. Comenta sobre el esfuerzo, la estrategia, la persistencia y el enfoque—no sobre rasgos innatos.

• Sé específico, no genérico. "Seguiste probando diferentes formas de hacerlo encajar" es más útil que "¡Buen trabajo!" que no tiene contenido real.

• Evita la inflación. El reconocimiento genuino y modesto es más útil para el desarrollo que los superlativos.

• Ve más allá del esfuerzo. Simplemente decir "trabajaste duro" no es suficiente—ayuda a los niños a identificar qué estrategias funcionaron y anímalos a explorar alternativas cuando algo no funciona.

• Cuidado con el lenguaje "fácil". Enmarcar tareas como fáciles para el niño ("¡eso es tan fácil para ti!") atribuye el desempeño a habilidad fija en lugar del proceso.

• Sé consciente del género. Las niñas estadísticamente reciben menos elogios de proceso y más elogios a la persona, contribuyendo a patrones motivacionales diferentes—vale la pena corregir activamente.

• Modela el fracaso como un evento de aprendizaje. La investigación de Haimovitz y Dweck muestra que los niños captan las creencias de los cuidadores sobre el fracaso mismo, no solo sus elogios.

El marco general es que la primera infancia es un período particularmente sensible para formar estas creencias—y que el elogio trata menos de hacer que los niños se sientan bien en el momento y más sobre formar su relación con el desafío, el esfuerzo y el aprendizaje a largo plazo.`
        }
      }
    },
    {
      id: 'reflect',
      icon: '🪞',
      gradient: gradients.purple,
      name: { en: "Connect and reflect", es: "Conectar y reflejar" },
      phrase: { en: "Like a mirror...", es: "Como un espejo..." },
      short: {
        en: "Mirror their actions and words. Reflecting nature play shows that the earth and all beings matter.",
        es: "Refleja sus acciones y palabras. Reflejar el juego con naturaleza muestra que la tierra y todos los seres importan."
      },
      full: {
        en: "Do what they do. If they tap, you tap. If they pick up a leaf, you do too. This says 'I'm with you.' Mirroring care for nature helps them feel connected to life.",
        es: "Haz lo que hacen. Si golpean, tú golpeas. Si recogen una hoja, tú también. Esto dice 'Estoy contigo.' Reflejar el cuidado por la naturaleza les ayuda a sentirse conectados a la vida."
      },
      examples: {
        en: ["Child taps two sticks → You tap yours: 'Tap tap! Like the woodpecker!'", "Child: 'It's a worm!' → You: 'A worm! Worms help the soil.'", "Child picks up a leaf → You pick up a leaf too", "Child hums → You hum the same tune"],
        es: ["Niño golpea dos palitos → Tú golpeas los tuyos: '¡Tap tap! ¡Como el pájaro carpintero!'", "Niño: '¡Es un gusano!' → Tú: '¡Un gusano! Los gusanos ayudan a la tierra.'", "Niño recoge una hoja → Tú recoges una hoja también", "Niño tararea → Tú tarareas lo mismo"]
      },
      tip: {
        en: "I'm with you. Earth and all beings matter.",
        es: "Estoy contigo. La tierra y todos los seres importan."
      }
    },
    {
      id: 'relate',
      icon: '🌱',
      gradient: gradients.green,
      name: { en: "Associate and relate", es: "Asociar y relacionar" },
      phrase: { en: "I'll add...", es: "Yo añado..." },
      short: {
        en: "Add something connected. Weaving in nature and sustainability expands how they see the world.",
        es: "Añade algo conectado. Tejer naturaleza y sostenibilidad amplía cómo ven el mundo."
      },
      full: {
        en: "Add to their play without taking over. Box-house → you add a leaf-garden. This invites curiosity and shows that caring for all life helps everything thrive.",
        es: "Añade a su juego sin tomar el control. Casa de caja → tú añades un jardín de hojas. Esto invita la curiosidad y muestra que cuidar toda la vida ayuda a todo a florecer."
      },
      examples: {
        en: ["Child makes box into car → You: 'I'll add a road. Where does your car go? Maybe to the park to plant a tree!'", "Child stacks rocks → You: 'I wonder what little creature lives under there. Maybe a bug!'", "Child makes sock puppet → You: 'Your puppet could tell a story about picking up trash at the beach.'"],
        es: ["Niño hace carro con caja → Tú: 'Yo añado un camino. ¿A dónde va tu carro? ¡Tal vez al parque a plantar un árbol!'", "Niño apila piedras → Tú: 'Me pregunto qué animalito vive ahí abajo. ¡Tal vez un bichito!'", "Niño hace títere de calcetín → Tú: 'Tu títere podría contar una historia sobre recoger basura en la playa.'"]
      },
      tip: {
        en: "Curiosity + care for all life → everything thrives.",
        es: "Curiosidad + cuidado de toda la vida → todo florece."
      }
    },
    {
      id: 'care',
      icon: '💚',
      gradient: gradients.pink,
      name: { en: "Sustainable thinking", es: "Pensamiento sostenible" },
      phrase: { en: "Care for all life...", es: "Cuidar toda la vida..." },
      short: {
        en: "Notice and name care for living things. When children see sustainability as valuable, they protect our shared world.",
        es: "Nota y nombra el cuidado por los seres vivos. Cuando los niños ven la sostenibilidad como valiosa, protegen nuestro mundo compartido."
      },
      full: {
        en: "Point out moments of care—watering plants, saving water, being gentle with creatures. This shows that all life matters and we're part of one living system.",
        es: "Señala momentos de cuidado—regar plantas, ahorrar agua, ser gentil con criaturas. Esto muestra que toda la vida importa y somos parte de un sistema vivo."
      },
      examples: {
        en: ["I see you being gentle with that ladybug. All creatures are important!", "You turned off the faucet while brushing—that saves water for plants and animals.", "Putting food scraps in compost helps feed the soil and new plants!", "You chose to reuse that jar. Less waste means a healthier planet for everyone."],
        es: ["Veo que eres gentil con esa catarina. ¡Todas las criaturas son importantes!", "Cerraste la llave mientras te cepillas—eso ahorra agua para plantas y animales.", "¡Poner restos de comida en composta ayuda a alimentar la tierra y nuevas plantas!", "Elegiste reutilizar ese frasco. Menos desperdicio significa un planeta más sano para todos."]
      },
      tip: {
        en: "All life matters. We're part of one living system.",
        es: "Toda la vida importa. Somos parte de un sistema vivo."
      }
    }
  ];

  // Game Tips with EcoGames
  const gameTips = [
    {
      id: 'animal-abcs',
      name: { en: "Animal ABCs, 123s", es: "ABCs de Animales" },
      icon: '📖',
      gradient: gradients.teal,
      skills: ['describe', 'praise'],
      tips: {
        en: [
          "DESCRIBE: 'You're tracing the letter A... now the alligator's teeth!'",
          "PRAISE: 'I love how carefully you're following the shape!'",
          "Let them go at their own pace—no rushing.",
          "🔄 EXTEND OFFLINE: Draw letters in sand, flour, or with sticks outside!"
        ],
        es: [
          "DESCRIBIR: '¡Estás trazando la letra A... ahora los dientes del caimán!'",
          "ELOGIAR: '¡Me encanta lo cuidadosamente que sigues la forma!'",
          "Déjalos ir a su propio ritmo—sin prisa.",
          "🔄 EXTIENDE SIN PANTALLA: ¡Dibuja letras en arena, harina o con palitos afuera!"
        ]
      }
    },
    {
      id: 'silly-animal',
      name: { en: "SillyGenerator", es: "SillyGenerator" },
      icon: '🎲',
      gradient: gradients.purple,
      skills: ['reflect', 'praise', 'relate'],
      tips: {
        en: [
          "REFLECT: When they laugh at a silly combo, laugh WITH them!",
          "PRAISE: 'You created that! A flying elephant-fish!'",
          "RELATE: 'What would it eat? Where would it sleep?'",
          "🔄 EXTEND OFFLINE: Draw the silly animal with crayons on scrap paper!"
        ],
        es: [
          "REFLEJAR: Cuando se rían de una combo tonta, ¡ríete CON ellos!",
          "ELOGIAR: '¡Tú creaste eso! ¡Un elefante-pez volador!'",
          "RELACIONAR: '¿Qué comería? ¿Dónde dormiría?'",
          "🔄 EXTIENDE SIN PANTALLA: ¡Dibuja el animal tonto con crayones en papel reciclado!"
        ]
      }
    },
    {
      id: 'creature-superpowers',
      name: { en: "Creature Superpowers", es: "Superpoderes de Criaturas" },
      icon: '🦸',
      gradient: gradients.pink,
      skills: ['describe', 'praise', 'relate'],
      tips: {
        en: [
          "DESCRIBE: 'You gave your creature wings AND super speed!'",
          "PRAISE: 'What an imaginative combination!'",
          "RELATE: 'I wonder what adventures your creature would go on...'",
          "🔄 EXTEND OFFLINE: Make the creature from recyclables—tubes, boxes, bottle caps!"
        ],
        es: [
          "DESCRIBIR: '¡Le diste a tu criatura alas Y super velocidad!'",
          "ELOGIAR: '¡Qué combinación tan imaginativa!'",
          "RELACIONAR: 'Me pregunto qué aventuras tendría tu criatura...'",
          "🔄 EXTIENDE SIN PANTALLA: ¡Haz la criatura con reciclables—tubos, cajas, tapas!"
        ]
      }
    },
    {
      id: 'echodraw',
      name: { en: "EchoDraw", es: "EchoDraw" },
      icon: '🎤',
      gradient: gradients.orange,
      skills: ['reflect', 'describe', 'praise'],
      tips: {
        en: [
          "REFLECT: Make sounds WITH them! Hum, shout, whisper together.",
          "DESCRIBE: 'Your loud sound made big splashes! Your whisper made tiny dots!'",
          "PRAISE: 'What a beautiful sound painting!'",
          "🔄 EXTEND OFFLINE: Bang pots, shake containers, make music with found sounds!"
        ],
        es: [
          "REFLEJAR: ¡Haz sonidos CON ellos! Tarareen, griten, susurren juntos.",
          "DESCRIBIR: '¡Tu sonido fuerte hizo grandes salpicaduras! ¡Tu susurro hizo puntitos!'",
          "ELOGIAR: '¡Qué hermosa pintura de sonido!'",
          "🔄 EXTIENDE SIN PANTALLA: ¡Golpea ollas, sacude contenedores, haz música con sonidos encontrados!"
        ]
      }
    },
    {
      id: 'pepe-knows',
      name: { en: "Pepe Knows", es: "Pepe Sabe" },
      icon: '🌳',
      gradient: gradients.cyan,
      skills: ['describe', 'relate'],
      tips: {
        en: [
          "DESCRIBE: 'Pepe is showing us about seeds... you're watching so carefully!'",
          "RELATE: 'I wonder if we could find seeds outside...'",
          "Follow their curiosity, not a script.",
          "🔄 EXTEND OFFLINE: Go on a nature walk and collect leaves, seeds, bark!"
        ],
        es: [
          "DESCRIBIR: 'Pepe nos está mostrando sobre semillas... ¡miras tan atentamente!'",
          "RELACIONAR: 'Me pregunto si podríamos encontrar semillas afuera...'",
          "Sigue su curiosidad, no un guión.",
          "🔄 EXTIENDE SIN PANTALLA: ¡Den un paseo por la naturaleza y recolecten hojas, semillas, corteza!"
        ]
      }
    },
    {
      id: 'benny-bunny',
      name: { en: "Benny The Bunny", es: "Benny El Conejo" },
      icon: '🐰',
      gradient: gradients.coral,
      skills: ['describe', 'praise'],
      tips: {
        en: [
          "DESCRIBE: 'Benny is looking for the path... you're helping him go left!'",
          "PRAISE: 'You figured out that puzzle! That was tricky!'",
          "If stuck, wait. Describe the problem, don't solve it for them.",
          "🔄 EXTEND OFFLINE: Make an obstacle course with pillows and furniture for YOUR bunny adventure!"
        ],
        es: [
          "DESCRIBIR: 'Benny está buscando el camino... ¡le ayudas a ir a la izquierda!'",
          "ELOGIAR: '¡Resolviste ese rompecabezas! ¡Era difícil!'",
          "Si se atoran, espera. Describe el problema, no lo resuelvas por ellos.",
          "🔄 EXTIENDE SIN PANTALLA: ¡Haz una carrera de obstáculos con cojines y muebles para TU aventura de conejo!"
        ]
      }
    },
    {
      id: 'peke-adventures',
      name: { en: "Peke's Adventures", es: "Aventuras de Peke" },
      icon: '🐕',
      gradient: gradients.green,
      skills: ['describe', 'relate'],
      tips: {
        en: [
          "DESCRIBE: 'Peke is exploring... you're choosing where to go!'",
          "RELATE: 'I wonder what Peke will find behind that tree...'",
          "Connect to feelings: 'How do you think Peke feels right now?'",
          "🔄 EXTEND OFFLINE: Go on a real adventure walk—explore your neighborhood like Peke would!"
        ],
        es: [
          "DESCRIBIR: 'Peke está explorando... ¡estás eligiendo a dónde ir!'",
          "RELACIONAR: 'Me pregunto qué encontrará Peke detrás de ese árbol...'",
          "Conecta con sentimientos: '¿Cómo crees que se siente Peke ahora?'",
          "🔄 EXTIENDE SIN PANTALLA: ¡Den un paseo de aventura real—exploren su vecindario como lo haría Peke!"
        ]
      }
    }
  ];

  // Offline Activities with CIRCULAR materials
  const offlineActivities = [
    {
      id: 'material-magic',
      emoji: '🎨',
      name: { en: "Material Magic", es: "Magia de Materiales" },
      gradient: gradients.teal,
      skillFocus: ['describe'],
      age: "2-5",
      duration: "15-20 min",
      summary: {
        en: "Explore art materials together while practicing the Describe skill",
        es: "Explora materiales de arte juntos mientras practicas Describir"
      },
      materials: {
        en: [
          "♻️ Scrap paper, junk mail, cardboard pieces",
          "✏️ Any crayons, pencils, or pens you have",
          "🍂 Optional: leaves, flower petals, sand, dirt",
          "🥄 Optional: flour or cornmeal for finger tracing"
        ],
        es: [
          "♻️ Papel de desecho, correo basura, pedazos de cartón",
          "✏️ Cualquier crayón, lápiz o pluma que tengas",
          "🍂 Opcional: hojas, pétalos, arena, tierra",
          "🥄 Opcional: harina o maicena para trazar con dedos"
        ]
      },
      ecoTip: {
        en: "The back of junk mail and cereal boxes make perfect canvases. Nature provides infinite textures—no store needed.",
        es: "La parte de atrás del correo basura y las cajas de cereal son lienzos perfectos. La naturaleza provee texturas infinitas—no se necesita tienda."
      },
      steps: {
        en: [
          "Set out 2-3 materials and let your child choose.",
          "Sit beside them and watch for one minute.",
          "Begin DESCRIBING: 'You're pressing hard... now soft...'",
          "Resist questions or suggestions. Just narrate.",
          "If invited, explore alongside them.",
          "After: 'Tell me about what you made.'"
        ],
        es: [
          "Pon 2-3 materiales y deja que tu hijo elija.",
          "Siéntate a su lado y observa por un minuto.",
          "Empieza a DESCRIBIR: 'Estás presionando fuerte... ahora suave...'",
          "Resiste preguntas o sugerencias. Solo narra.",
          "Si te invitan, explora junto a ellos.",
          "Después: 'Cuéntame sobre lo que hiciste.'"
        ]
      },
      reflection: {
        en: "What did you notice about your child's choices?",
        es: "¿Qué notaste sobre las elecciones de tu hijo?"
      }
    },
    {
      id: 'sound-mirrors',
      emoji: '🥁',
      name: { en: "Sound Mirrors", es: "Espejos de Sonido" },
      gradient: gradients.purple,
      skillFocus: ['reflect'],
      age: "2-5",
      duration: "10-15 min",
      summary: {
        en: "Make sounds and rhythms together, mirroring each other",
        es: "Hagan sonidos y ritmos juntos, reflejándose mutuamente"
      },
      materials: {
        en: [
          "🥫 Pots, pans, plastic containers, tin cans",
          "🥄 Wooden spoons, spatulas, chopsticks",
          "🫘 Dried beans or rice in a sealed container = shaker",
          "👏 Your hands, feet, and voice—always free!"
        ],
        es: [
          "🥫 Ollas, sartenes, contenedores de plástico, latas",
          "🥄 Cucharas de madera, espátulas, palillos",
          "🫘 Frijoles secos o arroz en contenedor cerrado = maraca",
          "👏 Tus manos, pies y voz—¡siempre gratis!"
        ]
      },
      ecoTip: {
        en: "Your home is full of instruments. A pot and wooden spoon is a drum. Rice in a bottle is a shaker. Your body makes infinite sounds.",
        es: "Tu casa está llena de instrumentos. Una olla y cuchara de madera es un tambor. Arroz en una botella es una maraca. Tu cuerpo hace sonidos infinitos."
      },
      steps: {
        en: [
          "Gather 'instruments' and sit facing your child.",
          "Let them pick first. You pick something different.",
          "Wait for their sound, then REFLECT it back.",
          "Mirror rhythm, volume, energy.",
          "Switch! You start, see if they mirror you.",
          "End making music together—no one leading."
        ],
        es: [
          "Reúne 'instrumentos' y siéntate frente a tu hijo.",
          "Deja que elijan primero. Tú elige algo diferente.",
          "Espera su sonido, luego REFLÉJALO.",
          "Refleja ritmo, volumen, energía.",
          "¡Intercambien! Tú empiezas, ve si te reflejan.",
          "Terminen haciendo música juntos—nadie lidera."
        ]
      },
      reflection: {
        en: "Did they notice you were mirroring them?",
        es: "¿Notaron que los estabas reflejando?"
      }
    },
    {
      id: 'feeling-faces',
      emoji: '😊',
      name: { en: "Feeling Faces", es: "Caritas de Emociones" },
      gradient: gradients.amber,
      skillFocus: ['praise', 'describe'],
      age: "3-5",
      duration: "20-25 min",
      summary: {
        en: "Create faces showing emotions and build feeling vocabulary",
        es: "Crea caras mostrando emociones y desarrolla vocabulario de sentimientos"
      },
      materials: {
        en: [
          "📦 Cardboard circles, paper plates, or any flat scraps",
          "✏️ Crayons, markers, or even mud/berry 'paint'",
          "🧶 Yarn scraps, string, or grass for hair",
          "🔘 Buttons, bottle caps, or seeds for eyes/nose"
        ],
        es: [
          "📦 Círculos de cartón, platos de papel o cualquier recorte plano",
          "✏️ Crayones, marcadores, o incluso 'pintura' de lodo/bayas",
          "🧶 Retazos de estambre, cuerda, o pasto para cabello",
          "🔘 Botones, tapas de botella, o semillas para ojos/nariz"
        ]
      },
      ecoTip: {
        en: "Cut circles from cereal boxes. Yarn scraps become hair. Buttons from old clothes become eyes. Everything gets a second life.",
        es: "Corta círculos de cajas de cereal. Retazos de estambre se convierten en cabello. Botones de ropa vieja se convierten en ojos. Todo tiene una segunda vida."
      },
      steps: {
        en: [
          "Make faces together: 'Show me happy! Sad! Mad!'",
          "Name feelings: Happy, Sad, Mad, Scared, Calm.",
          "Let them pick TWO feelings to make as faces.",
          "DESCRIBE and PRAISE as they create.",
          "Talk: 'When do you feel happy?'",
          "Display the faces to use later."
        ],
        es: [
          "Hagan caras juntos: '¡Muéstrame feliz! ¡Triste! ¡Enojado!'",
          "Nombra emociones: Feliz, Triste, Enojado, Asustado, Tranquilo.",
          "Deja que elijan DOS emociones para hacer caras.",
          "DESCRIBE y ELOGIA mientras crean.",
          "Hablen: '¿Cuándo te sientes feliz?'",
          "Exhibe las caras para usar después."
        ]
      },
      reflection: {
        en: "Which feeling was easiest for them to express?",
        es: "¿Qué emoción fue más fácil de expresar para ellos?"
      }
    },
    {
      id: 'story-seeds',
      emoji: '🌱',
      name: { en: "Story Seeds", es: "Semillas de Cuentos" },
      gradient: gradients.green,
      skillFocus: ['relate', 'describe'],
      age: "3-5",
      duration: "15-20 min",
      summary: {
        en: "Plant story seeds with found objects and imagination",
        es: "Planta semillas de cuentos con objetos encontrados e imaginación"
      },
      materials: {
        en: [
          "🧸 Any small toys, figurines, or stuffed animals",
          "🍂 Sticks, stones, leaves, pinecones from outside",
          "🛏️ A 'world': towel, blanket, cardboard box, baking tray",
          "🥡 Optional: sand, dirt, rice, or flour as 'terrain'"
        ],
        es: [
          "🧸 Cualquier juguete pequeño, figurita o peluche",
          "🍂 Palitos, piedras, hojas, piñas de afuera",
          "🛏️ Un 'mundo': toalla, manta, caja de cartón, bandeja",
          "🥡 Opcional: arena, tierra, arroz o harina como 'terreno'"
        ]
      },
      ecoTip: {
        en: "A towel is an ocean. A box is a cave. Stones are mountains. Sticks are trees. Nature and imagination cost nothing.",
        es: "Una toalla es un océano. Una caja es una cueva. Las piedras son montañas. Los palitos son árboles. La naturaleza y la imaginación no cuestan nada."
      },
      steps: {
        en: [
          "Set up a 'world'—blanket = meadow, towel = ocean.",
          "Offer items. Let them choose characters.",
          "Say: 'I wonder what story happens here...' Wait.",
          "DESCRIBE what you see happening.",
          "RELATE by adding: 'Maybe there's a river here...'",
          "Let them end it. Ask: 'What happened today?'"
        ],
        es: [
          "Prepara un 'mundo'—manta = pradera, toalla = océano.",
          "Ofrece objetos. Deja que elijan personajes.",
          "Di: 'Me pregunto qué historia pasa aquí...' Espera.",
          "DESCRIBE lo que ves que está pasando.",
          "RELACIONA añadiendo: 'Quizás hay un río aquí...'",
          "Déjalos terminar. Pregunta: '¿Qué pasó hoy?'"
        ]
      },
      reflection: {
        en: "What themes appeared in their story?",
        es: "¿Qué temas aparecieron en su historia?"
      }
    },
    {
      id: 'puppet-play',
      emoji: '🧦',
      name: { en: "Puppet Play", es: "Teatro de Títeres" },
      gradient: gradients.coral,
      skillFocus: ['describe', 'reflect', 'praise', 'relate'],
      age: "3-5",
      duration: "25-30 min",
      summary: {
        en: "Create puppets from scraps and bring them to life—using ALL skills!",
        es: "Crea títeres de retazos y dales vida—¡usando TODAS las habilidades!"
      },
      materials: {
        en: [
          "🧦 Old socks (mismatched = perfect!)",
          "🥄 Wooden spoons or sticks from outside",
          "📦 Paper bags from groceries",
          "✂️ Scraps: fabric, yarn, buttons, paper, leaves"
        ],
        es: [
          "🧦 Calcetines viejos (¡desparejados = perfectos!)",
          "🥄 Cucharas de madera o palitos de afuera",
          "📦 Bolsas de papel del mandado",
          "✂️ Retazos: tela, estambre, botones, papel, hojas"
        ]
      },
      ecoTip: {
        en: "That sock without a match? Now it's a dragon. That paper bag? A wizard. Old buttons become eyes. Nothing needs to be bought—only reimagined.",
        es: "¿Ese calcetín sin pareja? Ahora es un dragón. ¿Esa bolsa de papel? Un mago. Los botones viejos se convierten en ojos. Nada necesita comprarse—solo reimaginarse."
      },
      steps: {
        en: [
          "Make puppets together! At least one each.",
          "Talk about characters: 'Mine is shy...'",
          "DESCRIBE and PRAISE as they create.",
          "Let their puppet speak first.",
          "REFLECT their energy. RELATE by responding.",
          "Ask: 'Who should I be?' Let them direct.",
          "Puppets take a bow!"
        ],
        es: [
          "¡Hagan títeres juntos! Al menos uno cada quien.",
          "Hablen de personajes: 'El mío es tímido...'",
          "DESCRIBE y ELOGIA mientras crean.",
          "Deja que su títere hable primero.",
          "REFLEJA su energía. RELACIONA respondiendo.",
          "Pregunta: '¿Quién debo ser?' Déjalos dirigir.",
          "¡Los títeres hacen una reverencia!"
        ]
      },
      reflection: {
        en: "This uses all 4 skills! Which felt most natural?",
        es: "¡Esto usa las 4 habilidades! ¿Cuál se sintió más natural?"
      }
    }
  ];

  const openModal = (type: 'skill' | 'gameTip' | 'activity', item: any) => {
    setModalType(type);
    setSelectedItem(item);
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedItem(null);
  };

  /** Scripted sim outcomes only (no sim-coach LLM — avoids extra model text in-thread). */
  const applyHeuristicCaregiverOutcome = (kind: GameSimKind, replyText: string) => {
    const assessment = assessCaregiverResponseForKind(kind, replyText, lang);
    const coachRound = playSimCaregiverCoachRoundRef.current;

    const shouldCoach =
      assessment.level === 'weak' && coachRound < PLAY_SIM_MAX_COACH_MESSAGES;

    if (shouldCoach) {
      const content =
        coachRound === 0
          ? getSimulationConstructiveCaregiverFeedback(kind, lang)
          : getSimulationConstructiveCaregiverFeedbackRound2(kind, lang);
      setChatMessages((prev) => {
        const out = singleAssistantAfterLastUser(prev, replyText, content);
        chatMessagesRef.current = out;
        return out;
      });
      playSimCaregiverCoachRoundRef.current = coachRound + 1;
      return;
    }

    const softLand =
      assessment.level === 'weak' && coachRound >= PLAY_SIM_MAX_COACH_MESSAGES;

    setChatMessages((prev) => {
      const wrapContent = getSimulationCaregiverPhaseWrapUp(kind, lang, replyText, assessment.level);
      const combined = softLand
        ? `${getSimulationCaregiverSoftLanding(kind, lang)}\n\n${wrapContent}`
        : wrapContent;
      const out = singleAssistantAfterLastUser(prev, replyText, combined);
      chatMessagesRef.current = out;
      return out;
    });
    playSimCaregiverCoachRoundRef.current = 0;
    setPlaySim({ kind, step: 'complete' });
  };

  const runCaregiverCoach = async (
    kind: GameSimKind,
    messagesWithUser: { role: 'user' | 'assistant'; content: string }[]
  ) => {
    const lastUser = messagesWithUser[messagesWithUser.length - 1]?.content?.trim() ?? '';
    try {
      applyHeuristicCaregiverOutcome(kind, lastUser);
    } finally {
      setIsChatLoading(false);
      playSimChatSendBusyRef.current = false;
    }
  };

  const sendChatMessage = async (suggestion?: string) => {
    const text = (suggestion ?? chatInput.trim()).trim();
    if (!text || isChatLoading || playSimChatSendBusyRef.current) return;

    speechRec.stop();

    const userMsg = { role: 'user' as const, content: text };

    // Scripted play simulations (quick replies 0–3; no API)
    if (playSim?.step === 'await_first_input') {
      const kind = playSim.kind;
      playSimChatSendBusyRef.current = true;
      setChatMessages((prev) => [...prev, userMsg]);
      if (!suggestion) setChatInput('');
      setIsChatLoading(true);
      window.setTimeout(() => {
        setChatMessages((prev) => {
          const beat1 = getSimulationBeat1Skills(kind, lang, text);
          const out = singleAssistantAfterLastUser(prev, text, beat1);
          chatMessagesRef.current = out;
          return out;
        });
        setPlaySim({ kind, step: 'await_skill_choice' });
        setIsChatLoading(false);
        playSimChatSendBusyRef.current = false;
      }, 350);
      return;
    }
    if (playSim?.step === 'await_skill_choice') {
      const kind = playSim.kind;
      playSimChatSendBusyRef.current = true;
      setChatMessages((prev) => [...prev, userMsg]);
      if (!suggestion) setChatInput('');
      setIsChatLoading(true);
      window.setTimeout(() => {
        setChatMessages((prev) => {
          const beat2 = getSimulationBeat2Caregiver(kind, lang);
          const out = singleAssistantAfterLastUser(prev, text, beat2);
          chatMessagesRef.current = out;
          return out;
        });
        playSimCaregiverCoachRoundRef.current = 0;
        // Start conversational skill building flow
        const childObservation = text;
        setPlaySim({
          kind,
          step: 'await_skill_describe',
          childObservation,
          skillResponses: {}
        });
        // Add first skill prompt to chat with sound
        window.setTimeout(() => {
          const skillPrompt = getConversationalSkillPrompt('await_skill_describe', lang, childObservation);
          setChatMessages((prev) => [...prev, { role: 'assistant', content: skillPrompt }]);
          // Play chime for Describe skill
          playSkillChime(SKILL_DESIGN_TOKENS.describe.soundFreq);
        }, 500);
        setIsChatLoading(false);
        playSimChatSendBusyRef.current = false;
      }, 350);
      return;
    }
    // Handle conversational skill building steps
    if (
      playSim?.step === 'await_skill_describe' ||
      playSim?.step === 'await_skill_reflect' ||
      playSim?.step === 'await_skill_praise' ||
      playSim?.step === 'await_skill_relate' ||
      playSim?.step === 'await_skill_care'
    ) {
      const currentStep = playSim.step;
      const kind = playSim.kind;
      const childObservation = playSim.childObservation || text;
      const skillResponses = playSim.skillResponses || {};

      playSimChatSendBusyRef.current = true;
      setChatMessages((prev) => [...prev, userMsg]);
      if (!suggestion) setChatInput('');
      setIsChatLoading(true);

      // Store the user's response for this skill
      const skillKey = currentStep.replace('await_skill_', '') as 'describe' | 'reflect' | 'praise' | 'relate' | 'care';
      skillResponses[skillKey] = text;

      // Acknowledge the response
      window.setTimeout(() => {
        const acknowledgment = getSkillAcknowledgment(currentStep, lang, text);
        setChatMessages((prev) => [...prev, { role: 'assistant', content: acknowledgment }]);

        // Determine next step - WonderGuidance sequence
        const skillOrder: Array<'describe' | 'reflect' | 'praise' | 'relate' | 'care'> = [
          'describe',
          'praise',
          'reflect',
          'relate',
          'care',
        ];
        const currentIndex = skillOrder.indexOf(skillKey);

        if (currentIndex < skillOrder.length - 1) {
          // Move to next skill
          const nextSkillKey = skillOrder[currentIndex + 1];
          const nextStep = `await_skill_${nextSkillKey}` as PlaySimStep;

          setPlaySim({
            kind,
            step: nextStep,
            childObservation,
            skillResponses,
          });

          // Prompt for next skill with chime
          window.setTimeout(() => {
            const nextPrompt = getConversationalSkillPrompt(nextStep as any, lang, childObservation);
            setChatMessages((prev) => [...prev, { role: 'assistant', content: nextPrompt }]);
            // Play chime for next skill
            const skillToken = SKILL_DESIGN_TOKENS[nextSkillKey];
            playSkillChime(skillToken.soundFreq);
            setIsChatLoading(false);
            playSimChatSendBusyRef.current = false;
          }, 600);
        } else {
          // All skills complete - combine into full response
          const fullResponse = [
            skillResponses.describe,
            skillResponses.reflect,
            skillResponses.praise,
            skillResponses.relate,
            skillResponses.care,
          ]
            .filter(Boolean)
            .join(' ');

          // Continue with coaching/feedback as before
          window.setTimeout(() => {
            const combinedMsg = lang === 'en'
              ? `**Your complete response:**\n\n"${fullResponse}"`
              : `**Tu respuesta completa:**\n\n"${fullResponse}"`;
            setChatMessages((prev) => [...prev, { role: 'assistant', content: combinedMsg }]);

            // Play celebration chord - all skills complete! 🎉
            playCelebrationChord();

            // Move to caregiver coaching phase
            setPlaySim({ kind, step: 'await_caregiver', childObservation, skillResponses });

            // Run coaching on the combined response
            window.setTimeout(() => {
              const userResponseMsg = { role: 'user' as const, content: fullResponse };
              const messagesWithResponse = [...chatMessagesRef.current, userResponseMsg];
              void runCaregiverCoach(kind, messagesWithResponse);
            }, 800);

            setIsChatLoading(false);
            playSimChatSendBusyRef.current = false;
          }, 600);
        }
      }, 400);
      return;
    }

    if (playSim?.step === 'await_caregiver') {
      // This now only handles post-coaching interactions
      const kind = playSim.kind;
      playSimChatSendBusyRef.current = true;
      setIsChatLoading(true);
      setChatMessages((prev) => {
        const next = [...prev, userMsg];
        chatMessagesRef.current = next;
        void runCaregiverCoach(kind, next);
        return next;
      });
      if (!suggestion) setChatInput('');
      return;
    }

    playSimChatSendBusyRef.current = true;
    setChatMessages((prev) => [...prev, userMsg]);
    if (!suggestion) setChatInput('');
    setIsChatLoading(true);

    try {
      const res = await fetch('/api/play-together-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...chatMessages, userMsg].map((m) => ({ role: m.role, content: m.content })),
          language: lang,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed: ${res.status}`);
      }

      const data = await res.json();
      const assistantContent = data.message?.content ?? t('Sorry, I could not respond. Try again.', 'Lo siento, no pude responder. Intenta de nuevo.');
      setChatMessages((prev) => [...prev, { role: 'assistant', content: assistantContent }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('Something went wrong. Try again.', 'Algo salió mal. Intenta de nuevo.');
      setChatMessages((prev) => [...prev, { role: 'assistant', content: `❌ ${msg}` }]);
    } finally {
      setIsChatLoading(false);
      playSimChatSendBusyRef.current = false;
    }
  };

  /** Scripted pull line only — no LLM supplement (model text was unwanted in-thread). */
  const fetchOnboardingPullReply = async (
    _messagesWithUser: { role: 'user' | 'assistant'; content: string }[]
  ): Promise<string> => getPullBlock(lang);

  const advanceOnboardingContinue = () => {
    if (onboardingPullRequestLockRef.current) return;
    if (onboardingMicroStep !== 'awaiting_ready') return;
    onboardingPullRequestLockRef.current = true;
    const userLine = t(microLabels.userReady.en, microLabels.userReady.es);
    setOnboardingAwaitingPullAi(true);
    setIsChatLoading(true);

    const prev = chatMessagesRef.current;
    const next = [...prev, { role: 'user' as const, content: userLine }];
    chatMessagesRef.current = next;
    setChatMessages(next);

    const myGen = ++onboardingPullGenRef.current;
    void (async () => {
      try {
        const content = await fetchOnboardingPullReply(next);
        if (myGen !== onboardingPullGenRef.current) return;
        setChatMessages((p) => {
          const out = singleAssistantAfterLastUser(p, userLine, content);
          chatMessagesRef.current = out;
          return out;
        });
        setOnboardingMicroStep('awaiting_pull');
      } finally {
        onboardingPullRequestLockRef.current = false;
        if (myGen === onboardingPullGenRef.current) {
          setOnboardingAwaitingPullAi(false);
          setIsChatLoading(false);
        }
      }
    })();
  };

  const advancePlaySimSkillChoice = (en: string, es: string) => {
    if (!playSim) return;
    const kind = playSim.kind;
    const label = t(en, es);
    const childObservation = label; // The skill choice is the child's observation
    playSimCaregiverCoachRoundRef.current = 0;

    setChatMessages((prev) => [
      ...prev,
      { role: 'user', content: label },
      { role: 'assistant', content: getSimulationBeat2Caregiver(kind, lang) },
    ]);

    // Start conversational skill building
    setPlaySim({
      kind,
      step: 'await_skill_describe',
      childObservation,
      skillResponses: {}
    });

    // Add first skill prompt with chime
    window.setTimeout(() => {
      const skillPrompt = getConversationalSkillPrompt('await_skill_describe', lang, childObservation);
      setChatMessages((prev) => [...prev, { role: 'assistant', content: skillPrompt }]);
      // Play chime for Describe skill
      playSkillChime(SKILL_DESIGN_TOKENS.describe.soundFreq);
    }, 600);
  };

  /** Scripted Beat 0 only — no LLM bridge (the model added long, duplicate setup on top of the script). */
  const fetchOnboardingBridgeAndBeat0 = async (
    _messagesWithUser: { role: 'user' | 'assistant'; content: string }[],
    kind: GameSimKind
  ): Promise<string> => getSimulationBeat0(kind, lang);

  const advanceOnboardingQuickReply = (idx: number) => {
    const line = microLabels.quickReplies[idx];
    const userLine = t(line.userLine.en, line.userLine.es);
    if (idx >= 0 && idx < QUICK_REPLY_SIM_KIND.length) {
      if (onboardingBridgeRequestLockRef.current) return;
      onboardingBridgeRequestLockRef.current = true;
      const kind = QUICK_REPLY_SIM_KIND[idx]!;
      playSimCaregiverCoachRoundRef.current = 0;
      setPlaySim({ kind, step: 'await_first_input' });
      setIsChatLoading(true);

      const prev = chatMessagesRef.current;
      const userIdx = prev.length;
      const next = [...prev, { role: 'user' as const, content: userLine }];
      playSimThreadStartRef.current = userIdx;
      chatMessagesRef.current = next;
      setChatMessages(next);

      const myGen = ++onboardingBridgeGenRef.current;
      void (async () => {
        try {
          const combined = await fetchOnboardingBridgeAndBeat0(next, kind);
          if (myGen !== onboardingBridgeGenRef.current) return;
          setChatMessages((p) => {
            const out = singleAssistantAfterLastUser(p, userLine, combined);
            chatMessagesRef.current = out;
            return out;
          });
          setOnboardingMicroStep('show_games');
        } finally {
          onboardingBridgeRequestLockRef.current = false;
          setIsChatLoading(false);
        }
      })();
      return;
    }
  };

  const handleOnboardingTrySimulation = () => {
    void sendChatMessage(t(microLabels.trySimulation.en, microLabels.trySimulation.es));
  };

  const handleLandingBrowseOwn = () => {
    setLandingWelcomeVisible(false);
    setOnboardingMicroStep('idle');
    setPlaySim(null);
    playSimCaregiverCoachRoundRef.current = 0;
    setIsHubOpen(false);
    onLandingDismiss?.();
  };

  const handleLandingPickGame = (id: HomeGameScreen) => {
    setLandingWelcomeVisible(false);
    setOnboardingMicroStep('idle');
    setPlaySim(null);
    playSimCaregiverCoachRoundRef.current = 0;
    setIsHubOpen(false);
    onNavigateToGame?.(id);
    onLandingDismiss?.();
  };

  const handleLandingOpenGuide = () => {
    setLandingWelcomeVisible(false);
    setOnboardingMicroStep('idle');
    setPlaySim(null);
    playSimCaregiverCoachRoundRef.current = 0;
    setHubView('guide');
    onLandingDismiss?.();
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const closePrintView = () => {
    setShowPrintView(false);
  };

  /** Toggle fullscreen mode - like Netflix */
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      try {
        if (hubContainerRef.current) {
          await hubContainerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    } else {
      // Exit fullscreen
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error('Error exiting fullscreen:', err);
      }
    }
  };

  // Listen for fullscreen changes (e.g., user presses ESC)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  /** Restart onboarding flow from the beginning - for floating button */
  const restartOnboarding = () => {
    // Reset all state
    onboardingPullGenRef.current += 1;
    onboardingBridgeGenRef.current += 1;
    onboardingBridgeRequestLockRef.current = false;
    playSimChatSendBusyRef.current = false;

    // Open hub and show welcome
    setIsHubOpen(true);
    setLandingWelcomeVisible(true);
    setOnboardingMicroStep('awaiting_ready');

    // Reset chat to welcome message
    const welcome = [{ role: 'assistant' as const, content: getWelcomeBlock(lang) }];
    chatMessagesRef.current = welcome;
    setChatMessages(welcome);

    // Clear any active simulation
    setPlaySim(null);
    playSimCaregiverCoachRoundRef.current = 0;

    // Reset loading states
    setOnboardingAwaitingPullAi(false);
    setIsChatLoading(false);
  };

  const isLandingLayout = landingWelcomeVisible;

  const getPlaySimChatPlaceholder = (): string => {
    if (!landingWelcomeVisible || !playSim) {
      return t('Type anything you\'d like to ask...', 'Escribe lo que quieras preguntar...');
    }
    if (playSim.step === 'await_first_input') {
      switch (playSim.kind) {
        case 'literacy':
          return t('Name an animal (e.g. alligator, bear)...', 'Di o escribe un animal (p. ej. caimán, oso)...');
        case 'breathing':
          return t(
            'Something you notice outdoors + how it feels in your body...',
            'Algo que notes afuera + cómo lo sientes en el cuerpo...'
          );
        case 'draw':
          return t(
            'A creature, powers, colors—what will you imagine?',
            'Una criatura, poderes, colores—¿qué imaginas?'
          );
        case 'express':
          return t('A feeling word or idea (excited, curious)...', 'Palabra de sentimiento o idea (emocionado, curioso)...');
        default:
          return t('Type anything you\'d like to ask...', 'Escribe lo que quieras preguntar...');
      }
    }
    if (playSim.step === 'await_skill_choice') {
      return t('Which skill stood out—or tap a button above', 'Qué habilidad destacó—o toca un botón arriba');
    }
    if (playSim.step === 'await_caregiver') {
      return t('Your caregiver lines (2–3 short sentences)...', 'Tus frases como cuidador (2–3 cortas)...');
    }
    return t('Type anything you\'d like to ask...', 'Escribe lo que quieras preguntar...');
  };

  // Hub overlay content inlined (avoids remount on each keystroke when chatInput changes)
  const hubOverlayContent = isHubOpen && (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10050,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      animation: 'fadeIn 0.2s ease',
      /* Fullscreen - no background blur */
      background: 'transparent',
    }}
    >
      <div
        ref={hubContainerRef}
        style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: WG_PANEL_SURFACE,
        animation: 'slideIn 0.3s ease',
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Header — same surface as panel; dark type */}
        <div style={{
          flexShrink: 0,
          background: 'transparent',
          padding: '20px 24px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px',
          zIndex: 10,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', minWidth: 0 }}>
            <span style={{ fontSize: 'clamp(32px, 4.5vw, 40px)', lineHeight: 1, marginTop: '2px' }} aria-hidden>🤲</span>
            <div style={{ minWidth: 0 }}>
              <h2 style={wgHeaderTitleStyle}>
                {t('Play Together', 'Juguemos Juntos')}
              </h2>
              <p style={{ ...wgHeaderSubtitleStyle, marginTop: '2px', marginLeft: 'calc(-1 * (clamp(32px, 4.5vw, 40px) + 12px))' }}>
                {t('Supportive play for child development', 'Juego que apoya el desarrollo infantil')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            {/* Language segment styled to match GlobalNav's pill toggle */}
            <div
              className="flex rounded-md overflow-hidden border border-emerald-800/25"
              role="group"
              aria-label={t('Language', 'Idioma')}
            >
              <button
                type="button"
                onClick={() => setLanguage('en')}
                aria-pressed={language === 'en'}
                className={cn(
                  'min-w-[4.5rem] cursor-pointer px-3 py-1.5 text-sm font-semibold leading-snug transition-colors',
                  language === 'en'
                    ? 'bg-emerald-900 text-white'
                    : 'bg-transparent text-emerald-900/90 hover:bg-emerald-50',
                )}
                title={t('English', 'Inglés')}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLanguage('es')}
                aria-pressed={language === 'es'}
                className={cn(
                  'min-w-[4.5rem] cursor-pointer border-l border-emerald-800/20 px-3 py-1.5 text-sm font-semibold leading-snug transition-colors',
                  language === 'es'
                    ? 'bg-emerald-900 text-white'
                    : 'bg-transparent text-emerald-900/90 hover:bg-emerald-50',
                )}
                title={t('Spanish', 'Español')}
              >
                Español
              </button>
            </div>
            {/* Fullscreen button - like Netflix */}
            <button
              type="button"
              onClick={toggleFullscreen}
              style={{
                background: '#fff',
                border: '1px solid #D1D5DB',
                borderRadius: '10px',
                padding: '8px 12px',
                color: WG_TEXT.secondary,
                cursor: 'pointer',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }}
              title={isFullscreen ? t('Exit Fullscreen', 'Salir de pantalla completa') : t('Fullscreen', 'Pantalla completa')}
            >
              {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>
            <button
              type="button"
              onClick={() => setIsHubOpen(false)}
              style={{
                background: '#fff',
                border: '1px solid #D1D5DB',
                borderRadius: '10px',
                padding: '10px 16px',
                color: WG_TEXT.secondary,
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                lineHeight: 1,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }}
              title={t('Close', 'Cerrar')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab: Ask | Guide | Teaching Facilitation — always visible */}
        <div style={{
          flexShrink: 0,
          display: 'flex',
          gap: '10px',
          padding: '12px 20px 16px',
          background: 'transparent',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <button
            type="button"
            onClick={() => setHubView('ask')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: hubView === 'ask' ? '2px solid #166534' : '1px solid #E5E7EB',
              borderRadius: '12px',
              fontFamily: "'Nunito', sans-serif",
              fontSize: wgChromeMin,
              fontWeight: 700,
              cursor: 'pointer',
              background: hubView === 'ask' ? 'rgba(22, 101, 52, 0.06)' : '#fff',
              color: hubView === 'ask' ? '#14532D' : WG_TEXT.muted,
              boxShadow: 'none',
            }}
          >
            💬 {t('WonderGuidance', 'WonderGuidance')}
          </button>
          <button
            type="button"
            onClick={() => setHubView('guide')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: hubView === 'guide' ? '2px solid #166534' : '1px solid #E5E7EB',
              borderRadius: '12px',
              fontFamily: "'Nunito', sans-serif",
              fontSize: wgChromeMin,
              fontWeight: 700,
              cursor: 'pointer',
              background: hubView === 'guide' ? 'rgba(22, 101, 52, 0.06)' : '#fff',
              color: hubView === 'guide' ? '#14532D' : WG_TEXT.muted,
              boxShadow: 'none',
            }}
          >
            📖 {t('Guide', 'Guía')}
          </button>
          <button
            type="button"
            onClick={() => setHubView('teacher-facilitation')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: hubView === 'teacher-facilitation' ? '2px solid #166534' : '1px solid #E5E7EB',
              borderRadius: '12px',
              fontFamily: "'Nunito', sans-serif",
              fontSize: wgChromeMin,
              fontWeight: 700,
              cursor: 'pointer',
              background: hubView === 'teacher-facilitation' ? 'rgba(22, 101, 52, 0.06)' : '#fff',
              color: hubView === 'teacher-facilitation' ? '#14532D' : WG_TEXT.muted,
              boxShadow: 'none',
            }}
          >
            👩‍🏫 {t('Teaching', 'Enseñanza')}
          </button>
        </div>

        {/* Content: Guide or Chat */}
        <div style={{
          flex: 1,
          minHeight: 0,
          padding: landingWelcomeVisible ? 'clamp(16px, 2.5vmin, 28px) clamp(20px, 3.5vmin, 40px) clamp(20px, 2.5vmin, 32px)' : '20px',
          overflowY: landingWelcomeVisible ? 'auto' : hubView === 'guide' ? 'auto' : 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>

        {hubView === 'teacher-facilitation' ? (
          /* Teacher facilitation — full upgraded flow (session code → phases 1–9) lives in <TeachingFacilitation /> */
          <TeachingFacilitation onBack={() => setHubView('ask')} />
        ) : hubView === 'ask' ? (
          /* Chat UI — onboarding uses same thread when landing */
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0
          }}>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: landingWelcomeVisible ? '20px' : '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: landingWelcomeVisible ? '16px' : '12px'
            }}>
              {chatMessages.length === 0 && !landingWelcomeVisible && (
                <div style={{
                  padding: '24px',
                  background: '#F0FDF4',
                  borderRadius: '16px',
                  color: '#14532D',
                  ...wgBodyStyle,
                  fontSize: '18px',
                }}>
                  <p style={{ margin: '0 0 16px 0', fontWeight: 700 }}>
                    {t('Hello, welcome! We are thrilled you are here.', '¡Hola, bienvenido/a! Estamos emocionados de que estés aquí.')}
                  </p>
                  <p style={{ margin: '0 0 20px 0' }}>
                    {t(
                      'Tell me what interests you more about creative expressive and sustainable play.',
                      'Cuéntame qué te interesa más sobre el juego expresivo creativo y sostenible.'
                    )}
                  </p>
                  {!usedSuggestionTexts.has(t(microLabels.trySimulation.en, microLabels.trySimulation.es)) && (
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ margin: '0 0 10px 0', fontWeight: 700, fontSize: '18px' }}>
                        {t('Suggested next step', 'Siguiente paso sugerido')}
                      </p>
                      <button
                        type="button"
                        onClick={() => sendChatMessage(t(microLabels.trySimulation.en, microLabels.trySimulation.es))}
                        disabled={isChatLoading}
                        style={{
                          padding: '14px 18px',
                          background: 'linear-gradient(135deg, #166534 0%, #15803D 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          color: 'white',
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: 600,
                          textAlign: 'left',
                          cursor: isChatLoading ? 'not-allowed' : 'pointer',
                          opacity: isChatLoading ? 0.6 : 1,
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(22,101,52,0.2)'
                        }}
                        onMouseEnter={(e) => {
                          if (!isChatLoading) {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(22,101,52,0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,101,52,0.3)';
                        }}
                      >
                        {t(microLabels.trySimulation.en, microLabels.trySimulation.es)}
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filterUsedSuggestions([...playTogetherHubFollowUpChips]).map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => sendChatMessage(t(opt.en, opt.es))}
                        disabled={isChatLoading}
                        style={{
                          padding: '12px 16px',
                          background: 'white',
                          border: '2px solid #86EFAC',
                          borderRadius: '12px',
                          color: '#166534',
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          textAlign: 'left',
                          cursor: isChatLoading ? 'not-allowed' : 'pointer',
                          opacity: isChatLoading ? 0.6 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isChatLoading) {
                            e.currentTarget.style.background = '#DCFCE7';
                            e.currentTarget.style.borderColor = '#22C55E';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.borderColor = '#86EFAC';
                        }}
                      >
                        {t(opt.en, opt.es)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m, i) => {
                const isFirstWelcomeBubble =
                  landingWelcomeVisible && i === 0 && m.role === 'assistant';
                const assistantDisplay =
                  m.role === 'assistant'
                    ? formatMessageContent(
                        isFirstWelcomeBubble ? getWelcomeBlock(lang) : m.content
                      )
                    : m.content;

                // Check if this is a skill prompt message
                const skillColor = m.role === 'assistant' ? getSkillPromptColor(m.content) : null;

                return (
                <div
                  key={i}
                  style={{
                    ...wgBodyStyle,
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: isFirstWelcomeBubble ? '92%' : '88%',
                    padding: isFirstWelcomeBubble ? '28px 24px' : '16px 20px',
                    borderRadius: '16px',
                    fontSize: '18px',
                    letterSpacing: '0.01em',
                    background: m.role === 'user'
                      ? 'linear-gradient(135deg, #166534 0%, #15803D 100%)'
                      : skillColor || '#FFFFFF',
                    color: m.role === 'user' ? 'white' : WG_TEXT.primary,
                    boxShadow: m.role === 'user' ? '0 2px 8px rgba(0,0,0,0.08)' : skillColor ? '0 4px 16px rgba(0,0,0,0.12)' : '0 2px 12px rgba(0,0,0,0.06)',
                    border: m.role === 'assistant' ? (skillColor ? `2px solid ${skillColor}` : '1px solid #E5E7EB') : 'none',
                    transform: skillColor ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {assistantDisplay}
                </div>
                );
              })}
              {landingWelcomeVisible && playSim?.kind === 'literacy' && playSim.step === 'await_first_input' && (
                <AnimalAlphabetChatEmbed lang={lang} onPickAnimal={(name) => setChatInput(name)} />
              )}
              {landingWelcomeVisible && onboardingMicroStep === 'awaiting_ready' && !onboardingAwaitingPullAi && (
                <div style={{ alignSelf: 'stretch', paddingTop: 8, paddingBottom: 4 }}>
                  <div style={{ height: 28 }} aria-hidden />
                  <button
                    type="button"
                    onClick={advanceOnboardingContinue}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '14px 20px',
                      borderRadius: '14px',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: "'Nunito', sans-serif",
                      fontWeight: 800,
                      fontSize: '18px',
                      color: '#fff',
                      background: 'linear-gradient(135deg, #166534 0%, #15803D 100%)',
                      boxShadow: '0 4px 14px rgba(22,101,52,0.3)',
                    }}
                  >
                    {t(microLabels.continueCta.en, microLabels.continueCta.es)}
                  </button>
                </div>
              )}
              {landingWelcomeVisible && onboardingMicroStep === 'awaiting_pull' && !isChatLoading && (
                <div style={{ alignSelf: 'stretch', marginTop: 8 }}>
                  <p style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: wgChromeMin,
                    fontWeight: 700,
                    color: WG_TEXT.muted,
                    margin: '0 0 12px 0',
                  }}>
                    {t(microLabels.pullChipLead.en, microLabels.pullChipLead.es)}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {microLabels.quickReplies.map((qr, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => advanceOnboardingQuickReply(idx)}
                        style={{
                          padding: '12px 16px',
                          background: 'white',
                          border: '2px solid #86EFAC',
                          borderRadius: '12px',
                          color: '#166534',
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: 600,
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        {t(qr.userLine.en, qr.userLine.es)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {landingWelcomeVisible && onboardingMicroStep === 'show_games' && (!playSim || playSim.step === 'complete') && (
                <div style={{ alignSelf: 'stretch', marginTop: 12 }}>
                  {playSim?.step === 'complete' &&
                    (playSim.kind === 'literacy' ||
                      playSim.kind === 'breathing' ||
                      playSim.kind === 'draw' ||
                      playSim.kind === 'express') && (
                    <div
                      style={{
                        marginBottom: 20,
                        padding: '16px 0 4px',
                        borderBottom: '1px solid #E5E7EB',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          handleLandingPickGame(GAME_SIM_KIND_TO_HOME_SCREEN[playSim.kind])
                        }
                        disabled={isChatLoading}
                        style={{
                          width: '100%',
                          maxWidth: 420,
                          padding: '14px 18px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: isChatLoading ? 'not-allowed' : 'pointer',
                          fontFamily: "'Nunito', sans-serif",
                          fontWeight: 800,
                          fontSize: wgChromeMin,
                          color: '#fff',
                          background: 'linear-gradient(135deg, #166534 0%, #15803D 100%)',
                          boxShadow: '0 4px 14px rgba(22,101,52,0.25)',
                          opacity: isChatLoading ? 0.6 : 1,
                          marginBottom: 16,
                        }}
                      >
                        {playSim.kind === 'literacy'
                          ? t(microLabels.openAnimalAbcsCta.en, microLabels.openAnimalAbcsCta.es)
                          : playSim.kind === 'breathing'
                            ? t(microLabels.openPepeKnowsCta.en, microLabels.openPepeKnowsCta.es)
                            : playSim.kind === 'draw'
                              ? t(microLabels.openSuperpowersCta.en, microLabels.openSuperpowersCta.es)
                              : t(
                                  microLabels.openPekeAdventuresCta.en,
                                  microLabels.openPekeAdventuresCta.es
                                )}
                      </button>
                      <p
                        style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: 700,
                          color: WG_TEXT.muted,
                          margin: '0 0 10px 0',
                        }}
                      >
                        {t(
                          microLabels.postLiteracyOtherPracticesLead.en,
                          microLabels.postLiteracyOtherPracticesLead.es
                        )}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {microLabels.quickReplies.map((qr, idx) => (
                          <button
                            key={idx}
                            type="button"
                            disabled={isChatLoading}
                            onClick={() => advanceOnboardingQuickReply(idx)}
                            style={{
                              padding: '12px 16px',
                              background: 'white',
                              border: '2px solid #86EFAC',
                              borderRadius: '12px',
                              color: '#166534',
                              fontFamily: "'Nunito', sans-serif",
                              fontSize: wgChromeMin,
                              fontWeight: 600,
                              textAlign: 'left',
                              cursor: isChatLoading ? 'not-allowed' : 'pointer',
                              opacity: isChatLoading ? 0.6 : 1,
                            }}
                          >
                            {t(qr.userLine.en, qr.userLine.es)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <PlayTogetherGameCardsGrid
                    lang={lang}
                    onPickGame={handleLandingPickGame}
                    highlightedGameId={
                      playSim?.step === 'complete' && playSim.kind
                        ? GAME_SIM_KIND_TO_HOME_SCREEN[playSim.kind]
                        : undefined
                    }
                    highlightBadge={microLabels.suggestedGameTileBadge}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                    <button
                      type="button"
                      onClick={handleOnboardingTrySimulation}
                      disabled={isChatLoading}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: isChatLoading ? 'not-allowed' : 'pointer',
                        fontFamily: "'Nunito', sans-serif",
                        fontWeight: 700,
                        fontSize: wgChromeMin,
                        color: '#fff',
                        background: 'linear-gradient(135deg, #166534 0%, #15803D 100%)',
                        opacity: isChatLoading ? 0.6 : 1,
                      }}
                    >
                      {t(microLabels.trySimulation.en, microLabels.trySimulation.es)}
                    </button>
                    <button
                      type="button"
                      onClick={handleLandingOpenGuide}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '2px solid #86EFAC',
                        cursor: 'pointer',
                        fontFamily: "'Nunito', sans-serif",
                        fontWeight: 700,
                        fontSize: wgChromeMin,
                        color: '#166534',
                        background: '#fff',
                      }}
                    >
                      {t(microLabels.openGuide.en, microLabels.openGuide.es)}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setHubView('teacher-facilitation');
                        setLandingWelcomeVisible(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '2px solid #A78BFA',
                        cursor: 'pointer',
                        fontFamily: "'Nunito', sans-serif",
                        fontWeight: 700,
                        fontSize: wgChromeMin,
                        color: '#5B21B6',
                        background: '#fff',
                      }}
                    >
                      {t('Teaching Facilitation', 'Facilitación Docente')}
                    </button>
                  </div>
                  <p style={{
                    margin: '12px 0 0',
                    fontSize: wgChromeMin,
                    color: '#6B7280',
                    fontStyle: 'italic',
                    fontFamily: "'Nunito', sans-serif",
                    lineHeight: 1.5,
                  }}>
                    {t(microLabels.simHint.en, microLabels.simHint.es)}
                  </p>
                </div>
              )}
              {landingWelcomeVisible && playSim?.step === 'await_skill_choice' && (
                <div style={{ alignSelf: 'stretch', marginTop: 8 }}>
                  <p style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: wgChromeMin,
                    fontWeight: 700,
                    color: WG_TEXT.muted,
                    margin: '0 0 10px 0',
                  }}>
                    {t('Tap a skill or type your answer', 'Toca una habilidad o escribe tu respuesta')}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {literacySkillChoiceLabels.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        disabled={isChatLoading}
                        onClick={() => advancePlaySimSkillChoice(s.en, s.es)}
                        style={{
                          padding: '12px 16px',
                          background: 'white',
                          border: '2px solid #86EFAC',
                          borderRadius: '12px',
                          color: '#166534',
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: 600,
                          cursor: isChatLoading ? 'not-allowed' : 'pointer',
                          opacity: isChatLoading ? 0.6 : 1,
                        }}
                      >
                        {t(s.en, s.es)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {isChatLoading && (
                <div style={{
                  alignSelf: 'flex-start',
                  padding: '12px 16px',
                  borderRadius: '14px',
                  background: '#F3F4F6',
                  color: WG_TEXT.muted,
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: wgChromeMin,
                }}>
                  {t('Thinking...', 'Pensando...')} ✨
                </div>
              )}
              {/* Follow-up suggestion buttons - hide during simulation; show after simulation ends or on shift/slowness */}
              {!isChatLoading &&
                chatMessages.length > 0 &&
                (!landingWelcomeVisible || onboardingMicroStep === 'show_games') &&
                (!playSim || playSim.step === 'complete') &&
                shouldShowFollowUpButtons() &&
                (() => {
                const followUpOpts = filterUsedSuggestions([...playTogetherHubFollowUpChips]);
                return followUpOpts.length > 0 ? (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginTop: '4px'
                }}>
                  {followUpOpts.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => sendChatMessage(t(opt.en, opt.es))}
                      style={{
                        padding: '10px 14px',
                        background: 'white',
                        border: '2px solid #D1FAE5',
                        borderRadius: '10px',
                        color: '#166534',
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: wgChromeMin,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#D1FAE5';
                        e.currentTarget.style.borderColor = '#10B981';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#D1FAE5';
                      }}
                    >
                      {t(opt.en, opt.es)}
                    </button>
                  ))}
                </div>
                ) : null;
              })()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
              {playSimSpeechActive && speechRec.error && (
                <p style={{ margin: 0, fontSize: wgChromeMin, color: '#B45309', fontFamily: "'Nunito', sans-serif" }}>
                  {speechRec.error}
                </p>
              )}
              {/* Conversational skill building now happens in chat - no separate UI needed */}
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'stretch' }}>
                {playSimSpeechActive && (
                  <button
                    type="button"
                    aria-pressed={speechRec.listening}
                    aria-label={
                      speechRec.listening
                        ? t('Stop recording', 'Detener grabación')
                        : t('Speak with microphone', 'Hablar con el micrófono')
                    }
                    title={
                      !speechRec.supported
                        ? t('Voice input not supported in this browser', 'Voz no disponible en este navegador')
                        : speechRec.listening
                          ? t('Tap to stop', 'Toca para detener')
                          : t('Tap to speak', 'Toca para hablar')
                    }
                    disabled={isChatLoading || !speechRec.supported}
                    onClick={() => {
                      speechRec.clearError();
                      if (speechRec.listening) speechRec.stop();
                      else speechRec.start();
                    }}
                    style={{
                      flexShrink: 0,
                      minWidth: 52,
                      padding: '0 12px',
                      borderRadius: '12px',
                      border: speechRec.listening ? '2px solid #15803D' : '2px solid #E5E7EB',
                      background: speechRec.listening ? '#DCFCE7' : '#fff',
                      cursor: isChatLoading || !speechRec.supported ? 'not-allowed' : 'pointer',
                      fontSize: '22px',
                      lineHeight: 1,
                      opacity: !speechRec.supported ? 0.45 : 1,
                    }}
                  >
                    {speechRec.listening ? '⏹' : '🎤'}
                  </button>
                )}
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                  placeholder={getPlaySimChatPlaceholder()}
                  disabled={isChatLoading}
                  style={{
                    flex: 1,
                    padding: '14px 18px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: '18px',
                    outline: 'none',
                    color: WG_TEXT.primary,
                    background: '#fff',
                  }}
                />
                <button
                  type="button"
                  onClick={() => void sendChatMessage()}
                  disabled={isChatLoading || !chatInput.trim()}
                  style={{
                    padding: '14px 20px',
                    background: 'linear-gradient(135deg, #166534 0%, #15803D 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: wgChromeMin,
                    fontWeight: 700,
                    cursor: isChatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer',
                    opacity: isChatLoading || !chatInput.trim() ? 0.6 : 1
                  }}
                >
                  {t('Send', 'Enviar')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
          {/* Learning flow */}
          <div style={{
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
            border: '1px solid #6EE7B7',
            fontSize: wgChromeMin,
            fontFamily: "'Nunito', sans-serif",
            color: '#065F46',
            lineHeight: '1.5'
          }}>
            <span style={{ fontWeight: '700' }}>{t('Learning path:', 'Ruta de aprendizaje:')}</span>{' '}
            {t('1) Play with what you have → 2) 4 skills → 3) Eco games → 4) Hands-on → 5) Facilitated sessions', '1) Juega con lo que tienes → 2) 4 habilidades → 3) Eco juegos → 4) Con las manos → 5) Sesiones facilitadas')}
          </div>

          {/* Intro: 4 skills + sustainability */}
          <div style={{
            background: '#FEF3C7',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center',
            border: '2px dashed #F59E0B'
          }}>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              color: '#78350F',
              margin: '0 0 8px 0',
              lineHeight: '1.5'
            }}>
              {t(
                "The 4 skills help you care for your child and our planet. When we care—for ourselves, others, and nature—we live sustainably. All life can thrive. This builds self-love, confidence, curiosity, and courage in your child.",
                "Las 4 habilidades te ayudan a cuidar a tu hijo y al planeta. Cuando cuidamos—de nosotros, otros y la naturaleza—vivimos de forma sostenible. Toda la vida puede florecer. Esto desarrolla amor propio, confianza, curiosidad y valentía en tu hijo."
              )}
            </p>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '1.1rem',
              fontWeight: '800',
              color: '#92400E',
              margin: 0
            }}>
              ✨ {t("Follow your child's lead", "Sigue el liderazgo de tu hijo/a")}
            </p>
          </div>

          {/* 4 Skills */}
          <h3 style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: wgChromeMin,
            fontWeight: '700',
            color: '#6B7280',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            🎯 {t('The 5 Play Skills', 'Las 5 Habilidades')}
          </h3>
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#374151',
            margin: '0 0 12px 0',
            lineHeight: '1.5'
          }}>
            {t(
              "Each skill supports your child and care for the planet. Simple, connected, sustainable.",
              "Cada habilidad apoya a tu hijo y el cuidado del planeta. Simple, conectado, sostenible."
            )}
          </p>
          {/* First 4 skills in 2x2 grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px',
            marginBottom: '10px'
          }}>
            {playSkills.slice(0, 4).map(skill => {
              const isExpanded = expandedSkillId === skill.id;

              // Get pastel version of gradient for expanded state
              const getPastelGradient = (gradient: string) => {
                // Convert solid gradients to light pastel versions
                const gradientMap: Record<string, string> = {
                  'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)': 'linear-gradient(135deg, #FCE7F3 0%, #FDF2F8 100%)',
                  'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)': 'linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 100%)',
                  'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)': 'linear-gradient(135deg, #FEF3C7 0%, #FEF9E6 100%)',
                  'linear-gradient(135deg, #10B981 0%, #34D399 100%)': 'linear-gradient(135deg, #D1FAE5 0%, #ECFDF5 100%)',
                };
                return gradientMap[gradient] || 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)';
              };

              return (
                <button
                  key={skill.id}
                  onClick={() => setExpandedSkillId(isExpanded ? null : skill.id)}
                  style={{
                    background: isExpanded ? getPastelGradient(skill.gradient) : skill.gradient,
                    borderRadius: '14px',
                    border: 'none',
                    padding: isExpanded ? '20px' : '16px 12px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    color: isExpanded ? '#1F2937' : 'white',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                    width: '100%',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.15)';
                  }}
                >
                  {/* Collapsed state */}
                  {!isExpanded && (
                    <>
                      <div style={{ fontSize: '28px', marginBottom: '6px' }}>{skill.icon}</div>
                      <div style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: '18px',
                        fontWeight: '700'
                      }}>
                        {t(skill.name.en, skill.name.es)}
                      </div>
                      <div style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: wgChromeMin,
                        opacity: 0.9,
                        fontStyle: 'italic'
                      }}>
                        "{t(skill.phrase.en, skill.phrase.es)}"
                      </div>
                    </>
                  )}

                  {/* Expanded state */}
                  {isExpanded && (
                    <div style={{
                      animation: 'fadeIn 0.3s ease',
                      textAlign: 'left'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontSize: '32px' }}>{skill.icon}</div>
                        <div>
                          <div style={{
                            fontFamily: "'Nunito', sans-serif",
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#1F2937'
                          }}>
                            {t(skill.name.en, skill.name.es)}
                          </div>
                          <div style={{
                            fontFamily: "'Nunito', sans-serif",
                            fontSize: wgChromeMin,
                            fontStyle: 'italic',
                            color: '#6B7280'
                          }}>
                            "{t(skill.phrase.en, skill.phrase.es)}"
                          </div>
                        </div>
                      </div>

                      <p style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: '16px',
                        color: '#374151',
                        lineHeight: '1.6',
                        marginBottom: '16px',
                        marginTop: 0
                      }}>
                        {t(skill.full.en, skill.full.es)}
                      </p>

                      <div style={{
                        background: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '12px',
                        padding: '14px',
                        marginBottom: '14px',
                        border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <h4 style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: '700',
                          color: '#166534',
                          marginBottom: '8px',
                          marginTop: 0
                        }}>
                          {t('Try saying:', 'Intenta decir:')}
                        </h4>
                        <ul style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: '16px',
                          color: '#14532D',
                          paddingLeft: '16px',
                          margin: 0,
                          lineHeight: '1.7'
                        }}>
                          {t(skill.examples.en, skill.examples.es).map((ex: string, i: number) => (
                            <li key={i}>{ex}</li>
                          ))}
                        </ul>
                      </div>

                      <div style={{
                        background: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        border: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <span>💡</span>
                        <p style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: '16px',
                          color: '#92400E',
                          margin: 0,
                          fontWeight: '600'
                        }}>
                          {t(skill.tip.en, skill.tip.es)}
                        </p>
                      </div>

                      {/* Note section - only for skills with a note property (like praise) */}
                      {'note' in skill && (skill as any).note && (
                        <div style={{
                          background: 'rgba(251, 191, 36, 0.1)',
                          borderRadius: '10px',
                          padding: '12px',
                          border: '1px solid rgba(251, 191, 36, 0.3)',
                          marginTop: '8px'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedSkillNoteId(expandedSkillNoteId === skill.id ? null : skill.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              width: '100%',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span>📚</span>
                              <span style={{
                                fontFamily: "'Nunito', sans-serif",
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#92400E'
                              }}>
                                {t((skill as any).note.en.title, (skill as any).note.es.title)}
                              </span>
                            </div>
                            <span style={{
                              fontSize: '14px',
                              color: '#92400E',
                              transform: expandedSkillNoteId === skill.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}>
                              ▼
                            </span>
                          </button>
                          {expandedSkillNoteId === skill.id && (
                            <div style={{
                              fontFamily: "'Nunito', sans-serif",
                              fontSize: '15px',
                              color: '#78350F',
                              lineHeight: '1.7',
                              marginTop: '12px',
                              whiteSpace: 'pre-line',
                              animation: 'fadeIn 0.3s ease-in'
                            }}>
                              {t((skill as any).note.en.content, (skill as any).note.es.content)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* 5th skill - Sustainable thinking - Full width */}
          {playSkills[4] && (() => {
            const skill = playSkills[4];
            const isExpanded = expandedSkillId === skill.id;

            // Get pastel version of gradient for expanded state
            const getPastelGradient = (gradient: string) => {
              const gradientMap: Record<string, string> = {
                'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)': 'linear-gradient(135deg, #FCE7F3 0%, #FDF2F8 100%)',
                'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)': 'linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 100%)',
                'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)': 'linear-gradient(135deg, #FEF3C7 0%, #FEF9E6 100%)',
                'linear-gradient(135deg, #10B981 0%, #34D399 100%)': 'linear-gradient(135deg, #D1FAE5 0%, #ECFDF5 100%)',
              };
              return gradientMap[gradient] || 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)';
            };

            return (
              <button
                onClick={() => setExpandedSkillId(isExpanded ? null : skill.id)}
                style={{
                  background: isExpanded ? getPastelGradient(skill.gradient) : skill.gradient,
                  borderRadius: '14px',
                  border: 'none',
                  padding: '14px',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isExpanded ? '0 6px 20px rgba(0,0,0,0.15)' : '0 3px 10px rgba(0,0,0,0.1)',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isExpanded ? '10px' : '0'
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.1)';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '32px' }}>{skill.icon}</span>
                  <div>
                    <div style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: '20px',
                      fontWeight: '700',
                      color: isExpanded ? '#374151' : 'white',
                      marginBottom: '2px'
                    }}>
                      {t(skill.name.en, skill.name.es)}
                    </div>
                    <div style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: wgChromeMin,
                      color: isExpanded ? '#6B7280' : 'rgba(255, 255, 255, 0.9)'
                    }}>
                      {t(skill.phrase.en, skill.phrase.es)}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: '18px',
                    color: '#374151',
                    lineHeight: '1.6',
                    animation: 'fadeIn 0.3s ease-in'
                  }}>
                    {t(skill.short.en, skill.short.es)}
                  </div>
                )}
              </button>
            );
          })()}

          {/* Attribution Section - Prominent */}
          <details style={{
            background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '24px',
            border: '1px solid #C4B5FD',
            cursor: 'pointer'
          }}>
            <summary style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              fontWeight: '700',
              color: '#5B21B6',
              listStyle: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>💜</span>
              {t("Where these skills come from", "De dónde vienen estas habilidades")}
              <span style={{ marginLeft: 'auto', fontSize: wgChromeMin }}>▼</span>
            </summary>
            <div style={{ marginTop: '12px' }}>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#4C1D95',
                lineHeight: '1.6',
                margin: '0 0 10px 0'
              }}>
                {t(attribution.en.story, attribution.es.story)}
              </p>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#4C1D95',
                lineHeight: '1.6',
                margin: '0 0 10px 0'
              }}>
                {t(attribution.en.context, attribution.es.context)}
              </p>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#4C1D95',
                lineHeight: '1.6',
                margin: '0 0 10px 0'
              }}>
                {t(attribution.en.gratitude, attribution.es.gratitude)}
              </p>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: wgChromeMin,
                color: '#6D28D9',
                margin: '0 0 10px 0',
                fontStyle: 'italic'
              }}>
                {t(attribution.en.encourage, attribution.es.encourage)}
              </p>
              <a
                href="https://caring.columbia.edu/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#7C3AED',
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  fontSize: wgChromeMin,
                  fontWeight: '700',
                  textDecoration: 'none',
                  marginBottom: '10px'
                }}
              >
                🔗 {t(attribution.en.link, attribution.es.link)}
              </a>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: wgChromeMin,
                color: '#7C3AED',
                margin: 0,
                fontStyle: 'italic'
              }}>
                {t(attribution.en.disclaimer, attribution.es.disclaimer)}
              </p>
            </div>
          </details>

          {/* Resourcefulness Section */}
          <div style={{
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #A7F3D0'
          }}>
            <h3 style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '1rem',
              fontWeight: '800',
              color: '#065F46',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ♻️ {t(resourcefulnessIntro.en.title, resourcefulnessIntro.es.title)}
            </h3>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              color: '#047857',
              marginBottom: '12px',
              fontStyle: 'italic'
            }}>
              {t(resourcefulnessIntro.en.subtitle, resourcefulnessIntro.es.subtitle)}
            </p>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              color: '#065F46',
              lineHeight: '1.5',
              marginBottom: '12px'
            }}>
              {t(resourcefulnessIntro.en.description, resourcefulnessIntro.es.description)}
            </p>
            <ul style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              color: '#065F46',
              lineHeight: '1.7',
              paddingLeft: '0',
              margin: 0,
              listStyle: 'none'
            }}>
              {t(resourcefulnessIntro.en.tips, resourcefulnessIntro.es.tips).map((tip, i) => (
                <li key={i} style={{ marginBottom: '4px' }}>{tip}</li>
              ))}
            </ul>
          </div>

          {/* EcoGames Tips */}
          <h3 style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: wgChromeMin,
            fontWeight: '700',
            color: '#6B7280',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            🎮 {t('With Your EcoGames', 'Con Tus EcoGames')}
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '24px'
          }}>
            {gameTips.map(game => {
              const isExpanded = expandedGameTipId === game.id;
              return (
                <div
                  key={game.id}
                  style={{
                    background: 'white',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    boxShadow: isExpanded ? '0 4px 16px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  <button
                    onClick={() => setExpandedGameTipId(isExpanded ? null : game.id)}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: isExpanded ? '12px 12px 0 0' : '12px',
                      padding: '12px 14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.style.borderColor = '#9CA3AF';
                          parent.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.style.borderColor = '#E5E7EB';
                          parent.style.boxShadow = 'none';
                        }
                      }
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: game.gradient,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      flexShrink: 0
                    }}>
                      {game.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#1F2937'
                      }}>
                        {t(game.name.en, game.name.es)}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '4px',
                        marginTop: '4px'
                      }}>
                        {playSkills.filter(s => game.skills.includes(s.id)).map(skill => (
                          <span key={skill.id} style={{ fontSize: '18px' }}>{skill.icon}</span>
                        ))}
                      </div>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '16px',
                      transition: 'transform 0.3s ease',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transformOrigin: 'center',
                      color: '#9CA3AF'
                    }}>
                      ▼
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{
                      padding: '20px',
                      paddingTop: '10px',
                      background: '#FAFAFA'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        marginBottom: '16px',
                        flexWrap: 'wrap'
                      }}>
                        {playSkills.filter(s => game.skills.includes(s.id)).map((skill: any) => (
                          <span key={skill.id} style={{
                            background: skill.gradient,
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '10px',
                            fontSize: wgChromeMin,
                            fontWeight: '600',
                            fontFamily: "'Nunito', sans-serif"
                          }}>
                            {skill.icon} {t(skill.name.en, skill.name.es)}
                          </span>
                        ))}
                      </div>

                      <h3 style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#374151',
                        marginBottom: '12px',
                        marginTop: 0
                      }}>
                        🤝 {t('Play Together Tips', 'Consejos para Jugar Juntos')}
                      </h3>
                      <ul style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: '16px',
                        color: '#1F2937',
                        paddingLeft: '0',
                        margin: 0,
                        listStyle: 'none'
                      }}>
                        {t(game.tips.en, game.tips.es).map((tip: string, i: number) => (
                          <li key={i} style={{
                            marginBottom: '10px',
                            padding: '12px',
                            background: tip.startsWith('🔄') ? '#ECFDF5' : (i % 2 === 0 ? '#F9FAFB' : '#FEF3C7'),
                            borderRadius: '10px',
                            lineHeight: '1.5',
                            fontWeight: tip.startsWith('🔄') ? '600' : '400',
                            color: tip.startsWith('🔄') ? '#065F46' : '#1F2937'
                          }}>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Offline Activities */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <h3 style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: wgChromeMin,
              fontWeight: '700',
              color: '#6B7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: 0
            }}>
              🖐️ {t('Hands-On (Screen-Free)', 'Con las Manos (Sin Pantalla)')}
            </h3>
            <button
              onClick={handlePrint}
              style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                color: 'white',
                fontSize: wgChromeMin,
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              🖨️ {t('Print', 'Imprimir')}
            </button>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '24px'
          }}>
            {offlineActivities.map(activity => {
              const isExpanded = expandedActivityId === activity.id;
              return (
                <div
                  key={activity.id}
                  style={{
                    background: activity.gradient,
                    borderRadius: '14px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    boxShadow: isExpanded ? '0 6px 24px rgba(0,0,0,0.2)' : '0 3px 12px rgba(0,0,0,0.15)'
                  }}
                >
                  <button
                    onClick={() => setExpandedActivityId(isExpanded ? null : activity.id)}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: isExpanded ? '14px 14px 0 0' : '14px',
                      padding: '16px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        const parent = e.currentTarget.parentElement;
                        if (parent) parent.style.boxShadow = '0 8px 28px rgba(0,0,0,0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        const parent = e.currentTarget.parentElement;
                        if (parent) parent.style.boxShadow = '0 3px 12px rgba(0,0,0,0.15)';
                      }
                    }}
                  >
                    <div style={{ fontSize: '32px' }}>{activity.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: '1rem',
                        fontWeight: '700',
                        marginBottom: '2px'
                      }}>
                        {t(activity.name.en, activity.name.es)}
                      </div>
                      <div style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: wgChromeMin,
                        opacity: 0.9
                      }}>
                        ⏱️ {activity.duration} • ♻️ {t('Found materials', 'Materiales encontrados')}
                      </div>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '16px',
                      transition: 'transform 0.3s ease',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transformOrigin: 'center'
                    }}>
                      ▼
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.98)',
                      padding: '20px',
                      animation: 'slideDown 0.3s ease',
                      color: '#1F2937'
                    }}>
                      <div style={{
                        fontSize: wgChromeMin,
                        color: '#6B7280',
                        marginBottom: '12px'
                      }}>
                        👶 {activity.age} {t('years', 'años')} • ⏱️ {activity.duration}
                      </div>

                      {/* Eco Tip */}
                      <div style={{
                        background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                        borderRadius: '12px',
                        padding: '14px',
                        marginBottom: '16px',
                        border: '1px solid #A7F3D0'
                      }}>
                        <p style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: '16px',
                          color: '#065F46',
                          margin: 0,
                          fontWeight: '600'
                        }}>
                          ♻️ {t(activity.ecoTip.en, activity.ecoTip.es)}
                        </p>
                      </div>

                      {/* Materials */}
                      <div style={{
                        background: '#F0F9FF',
                        borderRadius: '12px',
                        padding: '14px',
                        marginBottom: '14px'
                      }}>
                        <h4 style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: '700',
                          color: '#0369A1',
                          marginBottom: '8px',
                          marginTop: 0
                        }}>
                          📦 {t("Gather (from around your home!)", "Reúne (¡de alrededor de tu casa!)")}
                        </h4>
                        <ul style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: '16px',
                          color: '#0C4A6E',
                          paddingLeft: '0',
                          margin: 0,
                          listStyle: 'none',
                          lineHeight: '1.6'
                        }}>
                          {t(activity.materials.en, activity.materials.es).map((m: string, i: number) => (
                            <li key={i}>{m}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Steps */}
                      <div style={{
                        background: '#F0FDF4',
                        borderRadius: '12px',
                        padding: '14px',
                        marginBottom: '14px'
                      }}>
                        <h4 style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: wgChromeMin,
                          fontWeight: '700',
                          color: '#166534',
                          marginBottom: '8px',
                          marginTop: 0
                        }}>
                          🎯 {t("How to play", "Cómo jugar")}
                        </h4>
                        <ol style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: '16px',
                          color: '#14532D',
                          paddingLeft: '18px',
                          margin: 0,
                          lineHeight: '1.7'
                        }}>
                          {t(activity.steps.en, activity.steps.es).map((s: string, i: number) => (
                            <li key={i} style={{ marginBottom: '6px' }}>{s}</li>
                          ))}
                        </ol>
                      </div>

                      {/* Reflection */}
                      <div style={{
                        background: '#FEF3C7',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px'
                      }}>
                        <span>💭</span>
                        <div>
                          <p style={{
                            fontFamily: "'Nunito', sans-serif",
                            fontSize: wgChromeMin,
                            fontWeight: '700',
                            color: '#92400E',
                            margin: '0 0 2px 0'
                          }}>
                            {t("Reflect afterward:", "Reflexiona después:")}
                          </p>
                          <p style={{
                            fontFamily: "'Nunito', sans-serif",
                            fontSize: '16px',
                            color: '#92400E',
                            margin: 0,
                            fontStyle: 'italic'
                          }}>
                            {t(activity.reflection.en, activity.reflection.es)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Facilitated Sessions Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            border: '2px solid #6EE7B7',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.15)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '50%',
                marginBottom: '12px',
                fontSize: '28px'
              }}>
                🤝
              </div>
              <h3 style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '1.3rem',
                fontWeight: '800',
                color: '#065F46',
                marginBottom: '10px'
              }}>
                {t(
                  'Interested in online facilitated sessions?',
                  '¿Interesado en sesiones facilitadas en línea?'
                )}
              </h3>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#047857',
                lineHeight: '1.6',
                marginBottom: '16px',
                maxWidth: '400px',
                margin: '0 auto 16px'
              }}>
                {t(
                  'Create these hands-on activities together in a guided online session.',
                  'Crea estas actividades prácticas juntos en una sesión guiada en línea.'
                )}
              </p>
              <a
                href={`mailto:info@wonderpath.nyc?subject=${encodeURIComponent(t('Interest in Play Together Facilitated Sessions', 'Interés en Sesiones Facilitadas de Juguemos Juntos'))}&body=${encodeURIComponent(t('Hi, I would like to learn more about facilitated sessions for hands-on activities.', 'Hola, me gustaría aprender más sobre sesiones facilitadas para actividades prácticas.'))}`}
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: '700',
                  textDecoration: 'none',
                  fontFamily: "'Nunito', sans-serif",
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                {t('Sign Up Here!', '¡Regístrate Aquí!')}
              </a>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: wgChromeMin,
                color: '#059669',
                marginTop: '12px',
                fontStyle: 'italic'
              }}>
                {t(
                  'Connect with a facilitator to explore these activities together',
                  'Conéctate con un facilitador para explorar estas actividades juntos'
                )}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            textAlign: 'center',
            padding: '16px 0',
            borderTop: '1px solid #E5E7EB'
          }}>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              color: '#6B7280',
              margin: 0
            }}>
              🌱 Wonderpath Studio
            </p>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: wgChromeMin,
              color: '#9CA3AF',
              margin: '4px 0 0 0'
            }}>
              {t('Nurturing creativity and care', 'Cultivando creatividad y cuidado')}
            </p>
          </div>
          </>
        )}
        </div>
      </div>
    </div>
  );

  // Modal Component - defined as inner component but only used when modal is open (no typing inside)
  const Modal: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({ children, onClose }) => (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      zIndex: 1000
    }}
    onClick={onClose}
    >
      <div style={{
        background: '#FFFEF9',
        borderRadius: '20px',
        maxWidth: '440px',
        width: '100%',
        maxHeight: '85vh',
        overflowY: 'auto',
        position: 'relative',
        animation: 'scaleIn 0.2s ease',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'sticky',
            top: '12px',
            float: 'right',
            marginRight: '12px',
            background: 'rgba(0,0,0,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            fontSize: '16px',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );

  // Skill Modal
  const SkillModal: React.FC<{ skill: any }> = ({ skill }) => (
    <>
      <div style={{
        background: skill.gradient,
        padding: '36px 20px 28px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>{skill.icon}</div>
        <h2 style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '1.6rem',
          fontWeight: '800',
          marginBottom: '4px'
        }}>
          {t(skill.name.en, skill.name.es)}
        </h2>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '1rem',
          opacity: 0.9,
          fontStyle: 'italic'
        }}>
          "{t(skill.phrase.en, skill.phrase.es)}"
        </p>
      </div>
      <div style={{ padding: '20px' }}>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '18px',
          color: '#374151',
          lineHeight: '1.6',
          marginBottom: '16px'
        }}>
          {t(skill.full.en, skill.full.es)}
        </p>
        <div style={{
          background: '#F0FDF4',
          borderRadius: '12px',
          padding: '14px',
          marginBottom: '14px'
        }}>
          <h4 style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: wgChromeMin,
            fontWeight: '700',
            color: '#166534',
            marginBottom: '8px'
          }}>
            {t('Try saying:', 'Intenta decir:')}
          </h4>
          <ul style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#14532D',
            paddingLeft: '16px',
            margin: 0,
            lineHeight: '1.7'
          }}>
            {t(skill.examples.en, skill.examples.es).map((ex: string, i: number) => (
              <li key={i}>{ex}</li>
            ))}
          </ul>
        </div>
        <div style={{
          background: '#FEF3C7',
          borderRadius: '10px',
          padding: '12px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px'
        }}>
          <span>💡</span>
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#92400E',
            margin: 0,
            fontWeight: '600'
          }}>
            {t(skill.tip.en, skill.tip.es)}
          </p>
        </div>

        {/* Note section - only for skills with a note property (like praise) */}
        {'note' in skill && (skill as any).note && (
          <div style={{
            background: 'rgba(251, 191, 36, 0.15)',
            borderRadius: '10px',
            padding: '14px',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            marginTop: '14px'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSkillNoteId(expandedSkillNoteId === skill.id ? null : skill.id);
              }}
              style={{
                background: 'none',
                border: 'none',
                width: '100%',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📚</span>
                <span style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#92400E'
                }}>
                  {t((skill as any).note.en.title, (skill as any).note.es.title)}
                </span>
              </div>
              <span style={{
                fontSize: '16px',
                color: '#92400E',
                transform: expandedSkillNoteId === skill.id ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </button>
            {expandedSkillNoteId === skill.id && (
              <div style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '17px',
                color: '#78350F',
                lineHeight: '1.7',
                marginTop: '14px',
                whiteSpace: 'pre-line',
                animation: 'fadeIn 0.3s ease-in'
              }}>
                {t((skill as any).note.en.content, (skill as any).note.es.content)}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  // Game Tips Modal
  const GameTipsModal: React.FC<{ game: any }> = ({ game }) => (
    <>
      <div style={{
        background: game.gradient,
        padding: '36px 20px 28px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>{game.icon}</div>
        <h2 style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '1.4rem',
          fontWeight: '800',
          marginBottom: '8px'
        }}>
          {t(game.name.en, game.name.es)}
        </h2>
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {playSkills.filter(s => game.skills.includes(s.id)).map((skill: any) => (
            <span key={skill.id} style={{
              background: 'rgba(255,255,255,0.25)',
              padding: '4px 10px',
              borderRadius: '10px',
              fontSize: wgChromeMin,
              fontWeight: '600'
            }}>
              {skill.icon} {t(skill.name.en, skill.name.es)}
            </span>
          ))}
        </div>
      </div>
      <div style={{ padding: '20px' }}>
        <h3 style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '18px',
          fontWeight: '700',
          color: '#374151',
          marginBottom: '12px'
        }}>
          🤝 {t('Play Together Tips', 'Consejos para Jugar Juntos')}
        </h3>
        <ul style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '18px',
          color: '#1F2937',
          paddingLeft: '0',
          margin: 0,
          listStyle: 'none'
        }}>
          {t(game.tips.en, game.tips.es).map((tip: string, i: number) => (
            <li key={i} style={{
              marginBottom: '10px',
              padding: '12px',
              background: tip.startsWith('🔄') ? '#ECFDF5' : (i % 2 === 0 ? '#F9FAFB' : '#FEF3C7'),
              borderRadius: '10px',
              lineHeight: '1.5',
              fontWeight: tip.startsWith('🔄') ? '600' : '400',
              color: tip.startsWith('🔄') ? '#065F46' : '#1F2937'
            }}>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  // Activity Modal
  const ActivityModal: React.FC<{ activity: any }> = ({ activity }) => (
    <>
      <div style={{
        background: activity.gradient,
        padding: '36px 20px 28px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>{activity.emoji}</div>
        <h2 style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: '1.4rem',
          fontWeight: '800',
          marginBottom: '6px'
        }}>
          {t(activity.name.en, activity.name.es)}
        </h2>
        <p style={{ fontSize: '18px', opacity: 0.9 }}>
          👶 {activity.age} {t('years', 'años')} • ⏱️ {activity.duration}
        </p>
      </div>
      <div style={{ padding: '20px' }}>

        {/* Eco Tip */}
        <div style={{
          background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
          borderRadius: '12px',
          padding: '14px',
          marginBottom: '16px',
          border: '1px solid #A7F3D0'
        }}>
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#065F46',
            margin: 0,
            fontWeight: '600'
          }}>
            ♻️ {t(activity.ecoTip.en, activity.ecoTip.es)}
          </p>
        </div>

        {/* Materials */}
        <div style={{
          background: '#F0F9FF',
          borderRadius: '12px',
          padding: '14px',
          marginBottom: '14px'
        }}>
          <h4 style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: wgChromeMin,
            fontWeight: '700',
            color: '#0369A1',
            marginBottom: '8px'
          }}>
            📦 {t("Gather (from around your home!)", "Reúne (¡de alrededor de tu casa!)")}
          </h4>
          <ul style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#0C4A6E',
            paddingLeft: '0',
            margin: 0,
            listStyle: 'none',
            lineHeight: '1.6'
          }}>
            {t(activity.materials.en, activity.materials.es).map((m: string, i: number) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>

        {/* Steps */}
        <div style={{
          background: '#F0FDF4',
          borderRadius: '12px',
          padding: '14px',
          marginBottom: '14px'
        }}>
          <h4 style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: wgChromeMin,
            fontWeight: '700',
            color: '#166534',
            marginBottom: '8px'
          }}>
            🎯 {t("How to play", "Cómo jugar")}
          </h4>
          <ol style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#14532D',
            paddingLeft: '18px',
            margin: 0,
            lineHeight: '1.7'
          }}>
            {t(activity.steps.en, activity.steps.es).map((s: string, i: number) => (
              <li key={i} style={{ marginBottom: '6px' }}>{s}</li>
            ))}
          </ol>
        </div>

        {/* Reflection */}
        <div style={{
          background: '#FEF3C7',
          borderRadius: '10px',
          padding: '12px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px'
        }}>
          <span>💭</span>
          <div>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: wgChromeMin,
              fontWeight: '700',
              color: '#92400E',
              margin: '0 0 2px 0'
            }}>
              {t("Reflect afterward:", "Reflexiona después:")}
            </p>
            <p style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '18px',
              color: '#92400E',
              margin: 0,
              fontStyle: 'italic'
            }}>
              {t(activity.reflection.en, activity.reflection.es)}
            </p>
          </div>
        </div>
      </div>
    </>
  );

  // Print View Component
  const PrintView = () => (
    <div className="print-view" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 2000,
      overflowY: 'auto',
      padding: '40px'
    }}>
      <button
        onClick={closePrintView}
        className="no-print"
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: '#7C3AED',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '8px 16px',
          cursor: 'pointer',
          fontWeight: '700'
        }}
      >
        ✕ {t('Close', 'Cerrar')}
      </button>

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '2rem',
            fontWeight: '800',
            color: '#166534',
            marginBottom: '8px'
          }}>
            🤲 {t('Play Together', 'Juguemos Juntos')}
          </h1>
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '1.1rem',
            color: '#047857',
            marginBottom: '4px'
          }}>
            {t('Hands-On Activities (Screen-Free)', 'Actividades con las Manos (Sin Pantalla)')}
          </p>
          <p style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: '#6B7280'
          }}>
            Wonderpath Studio
          </p>
        </div>

        {offlineActivities.map((activity, index) => (
          <div key={activity.id} style={{
            pageBreakInside: 'avoid',
            marginBottom: '40px',
            border: '2px solid #E5E7EB',
            borderRadius: '12px',
            padding: '20px',
            background: '#FAFAFA'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '2px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>{activity.emoji}</div>
              <h2 style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '1.5rem',
                fontWeight: '800',
                color: '#1F2937',
                marginBottom: '6px'
              }}>
                {t(activity.name.en, activity.name.es)}
              </h2>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#6B7280'
              }}>
                👶 {activity.age} {t('years', 'años')} • ⏱️ {activity.duration}
              </p>
            </div>

            {/* Summary */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.6'
              }}>
                {t(activity.summary.en, activity.summary.es)}
              </p>
            </div>

            {/* Eco Tip */}
            <div style={{
              background: '#ECFDF5',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              border: '1px solid #A7F3D0'
            }}>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#065F46',
                margin: 0,
                fontWeight: '600'
              }}>
                ♻️ {t(activity.ecoTip.en, activity.ecoTip.es)}
              </p>
            </div>

            {/* Materials */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '1rem',
                fontWeight: '700',
                color: '#0369A1',
                marginBottom: '8px'
              }}>
                📦 {t("What you'll need", "Lo que necesitarás")}
              </h3>
              <ul style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#1F2937',
                paddingLeft: '20px',
                margin: 0,
                lineHeight: '1.7'
              }}>
                {t(activity.materials.en, activity.materials.es).map((m: string, i: number) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>

            {/* Steps */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '1rem',
                fontWeight: '700',
                color: '#166534',
                marginBottom: '8px'
              }}>
                🎯 {t("How to play", "Cómo jugar")}
              </h3>
              <ol style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#1F2937',
                paddingLeft: '20px',
                margin: 0,
                lineHeight: '1.8'
              }}>
                {t(activity.steps.en, activity.steps.es).map((s: string, i: number) => (
                  <li key={i} style={{ marginBottom: '8px' }}>{s}</li>
                ))}
              </ol>
            </div>

            {/* Reflection */}
            <div style={{
              background: '#FEF3C7',
              borderRadius: '8px',
              padding: '12px',
              border: '1px solid #FCD34D'
            }}>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                fontWeight: '700',
                color: '#92400E',
                margin: '0 0 4px 0'
              }}>
                💭 {t("Reflect afterward:", "Reflexiona después:")}
              </p>
              <p style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: '18px',
                color: '#92400E',
                margin: 0,
                fontStyle: 'italic'
              }}>
                {t(activity.reflection.en, activity.reflection.es)}
              </p>
            </div>
          </div>
        ))}

        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '2px solid #E5E7EB',
          color: '#6B7280',
          fontSize: '18px'
        }}>
          <p>🌱 Wonderpath Studio • {t('Nurturing creativity and care', 'Cultivando creatividad y cuidado')}</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap');

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        @media print {
          .no-print {
            display: none !important;
          }

          body {
            margin: 0;
            padding: 0;
          }

          .print-view {
            position: static !important;
            padding: 20px !important;
          }
        }
      `}</style>

      {/* Floating button to open hub — brand-green pill with the
          WonderGuidance eyes 👀 in a lighter mint circle on the left.
          Matches the design-system green and Inter type. */}
      {!isHubOpen && (
        <button
          onClick={restartOnboarding}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            padding: '8px 26px 8px 8px',
            borderRadius: 999,
            background: '#42a57a',
            border: 'none',
            boxShadow: '0 6px 22px rgba(66, 165, 122, 0.32)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 20,
            fontWeight: 600,
            color: '#ffffff',
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            transition: 'all 0.3s ease',
            zIndex: 10040,
            animation: 'wgPulse 2.4s ease-in-out infinite',
          }}
          title={t('Play Together - WonderGuidance Practice', 'Juguemos Juntos - Práctica WonderGuidance')}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(66, 165, 122, 0.45)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(66, 165, 122, 0.32)'; }}
        >
          <span
            aria-hidden
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: '#a4d6b8',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            👀
          </span>
          <span>{t('WonderGuidance', 'WonderGuidance')}</span>
        </button>
      )}

      <style>{`
        @keyframes wgPulse {
          0%, 100% { box-shadow: 0 6px 22px rgba(66, 165, 122, 0.38); }
          50% { box-shadow: 0 8px 26px rgba(66, 165, 122, 0.55), 0 0 18px rgba(95, 189, 139, 0.35); }
        }
      `}</style>

      {/* The hub slides in from the right - inlined to avoid remount on keystroke */}
      {hubOverlayContent}

      {/* Detail modals */}
      {modalType && selectedItem && (
        <Modal onClose={closeModal}>
          {modalType === 'skill' && <SkillModal skill={selectedItem} />}
          {modalType === 'gameTip' && <GameTipsModal game={selectedItem} />}
          {modalType === 'activity' && <ActivityModal activity={selectedItem} />}
        </Modal>
      )}

      {/* Print View */}
      {showPrintView && <PrintView />}
    </>
  );
};

export default PlayTogetherFloatingHub;
